import React, { useState, useEffect, useCallback } from 'react';
import { batchService, customerService, productService, warehouseService, gatePassService, invoiceService, gdService, purchaseService } from '../../services/financeService';
import AttachmentPanel from './AttachmentPanel';
import SearchableSelect from './SearchableSelect';
import GatePassFormFields, { emptyForm, emptyItem, parsePackSizeKg, buildGatePassPayload, validateGatePassItems } from './GatePassFormFields';
import GatePassDetailModal from './GatePassDetailModal';
import WasteGradeModal, { STANDARD_WASTE_GRADES, createWasteGrade, isWasteGrade } from './WasteGradeModal';
import CsvActions from '../common/CsvActions';
import './Financial.css';

const BATCH_CSV_HEADERS = ['Batch Code', 'Product', 'Warehouse', 'Status', 'Gate Passes In', 'Gate Passes Out', 'Weight In KG', 'Weight Out KG', 'Money Spent', 'Money Earned', 'Created Date', 'Notes'];
const buildBatchCsvRows = (batches) => batches.map(b => [
  b.batchCode || '', b.productName || '', b.godownName || '',
  b.status || '', b.entryGatePasses ?? 0, b.exitGatePasses ?? 0, b.entryWeightKg ?? 0, b.exitWeightKg ?? 0,
  b.totalCost ?? 0, b.totalRevenue ?? 0,
  b.createdAt?.split('T')[0] || '', (b.notes || '').replace(/,/g, ';')
]);

// ── constants ─────────────────────────────────────────────────────────────────

// Sorting grades (Chaant, Jamboo, Goli, Safi...) are no longer a fixed list here: they are "waste
// grades" managed on the Grading tab (see WasteGradeModal.js), so new ones can be added on screen.

// Wastage reasons — merged in from the old standalone Wastage page (2026-08-18), same list.
const WASTAGE_REASONS = ['DAMAGED', 'EXPIRED', 'CONTAMINATED', 'PROCESSING_LOSS', 'SPILLAGE', 'REJECTED_QC', 'TRANSIT_DAMAGE', 'OTHER'];
const fmtReason = (v) => v ? v.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '';

// Bardana (packaging) options — locked to the real, confirmed values (2026-08-19), same
// "Other" escape hatch as Grade above.
const BARDANA_OPTIONS = ['Jute', 'PP Bag Yellow', 'PP Bag Red'];

// Operation types map: enum value → display label
const OP_TYPES = ['LOADING','PACKAGING_ONLY','SHIFTING','REPACKAGING','MARKETING','MISC','CUSTOM'];
const OP_LABEL = {
  LOADING:       'Loading (per kg)',
  PACKAGING_ONLY:'Packaging Only',
  SHIFTING:      'Shifting / Moving',
  REPACKAGING:   'Repackaging',
  MARKETING:     'Marketing',
  MISC:          'Miscellaneous',
  CUSTOM:        'Custom (see notes)',
};
const STATUSES     = ['OPEN','PROCESSING','EXPORTED','CLOSED'];

const STATUS_STYLE = {
  OPEN:       { background: 'var(--color-success-light)', color: 'var(--color-success)' },
  PROCESSING: { background: 'var(--color-warning-light)', color: 'var(--color-warning)' },
  EXPORTED:   { background: 'var(--color-info-light)', color: 'var(--color-primary)' },
  CLOSED:     { background: 'var(--color-bg-tertiary)', color: 'var(--color-neutral-700)' },
};

const pkr  = (v) => v == null ? '—' : 'PKR ' + Number(v).toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const usd  = (v) => v == null ? '—' : 'USD ' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const kg   = (v) => v == null ? '—' : Number(v).toLocaleString() + ' kg';
const fmt  = (s) => s ? s.replace(/_/g, ' ') : '—';

const badge = (text, styleMap) => {
  const s = styleMap[text] || { background: 'var(--color-bg-tertiary)', color: 'var(--color-neutral-700)' };
  return <span style={{ ...s, padding: '2px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: 600 }}>{text}</span>;
};

const Toast = ({ toast }) => toast ? (
  <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
    background: toast.type === 'error' ? 'var(--color-error)' : 'var(--color-success)',
    color: '#fff', padding: '12px 20px', borderRadius: 10, fontSize: 14, boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
    {toast.msg}
  </div>
) : null;

const Spinner = () => <div style={{ textAlign: 'center', padding: 40, color: 'var(--color-text-secondary)' }}>Loading…</div>;

const ConfirmModal = ({ message, onConfirm, onCancel }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onCancel}>
    <div style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 360, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }} onClick={e => e.stopPropagation()}>
      <p style={{ fontSize: 15, color: 'var(--color-neutral-700)', marginBottom: 20 }}>{message}</p>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button style={{ padding: '7px 18px', borderRadius: 6, border: '1px solid var(--color-border-strong)', background: 'var(--color-neutral-50)', cursor: 'pointer', fontSize: 13 }} onClick={onCancel}>Cancel</button>
        <button style={{ padding: '7px 18px', borderRadius: 6, border: 'none', background: 'var(--color-error)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }} onClick={onConfirm}>Delete</button>
      </div>
    </div>
  </div>
);

const Input = ({ label, hint, ...props }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    {label && <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', fontWeight: 500 }}>{label}</label>}
    <input {...props} style={{ padding: '7px 10px', border: '1px solid var(--color-border-strong)', borderRadius: 6, fontSize: 13, ...props.style }} />
    {hint && <span style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>{hint}</span>}
  </div>
);

// Locked dropdown with an explicit "Other" escape hatch — real confirmed values are the only
// way to select silently; anything else has to be deliberately typed, so it can't happen by typo.
const LockedSelect = ({ label, options, value, onChange, placeholder }) => {
  const isKnown = options.includes(value);
  // "Other" mode is sticky once chosen (tracked separately from value) so the free-text box
  // doesn't disappear the instant the user clears it back to empty while typing.
  const [forceOther, setForceOther] = useState(value !== '' && !isKnown);
  const showOther = forceOther || (value !== '' && !isKnown);
  const selectValue = showOther ? '__other__' : value;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {label && <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', fontWeight: 500 }}>{label}</label>}
      <select value={selectValue} onChange={e => {
          if (e.target.value === '__other__') { setForceOther(true); onChange(''); }
          else { setForceOther(false); onChange(e.target.value); }
        }}
        style={{ padding: '7px 10px', border: '1px solid var(--color-border-strong)', borderRadius: 6, fontSize: 13 }}>
        <option value="">Select...</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
        <option value="__other__">Other (type below)</option>
      </select>
      {showOther && (
        <input value={value} onChange={e => onChange(e.target.value)}
          placeholder={placeholder || 'Type value...'} autoFocus
          style={{ padding: '7px 10px', border: '1px solid var(--color-border-strong)', borderRadius: 6, fontSize: 13 }} />
      )}
    </div>
  );
};

const Select = ({ label, options, ...props }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    {label && <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', fontWeight: 500 }}>{label}</label>}
    <select {...props} style={{ padding: '7px 10px', border: '1px solid var(--color-border-strong)', borderRadius: 6, fontSize: 13, background: '#fff', ...props.style }}>
      <option value="">— select —</option>
      {options.map(o => typeof o === 'string'
        ? <option key={o} value={o}>{fmt(o)}</option>
        : <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const today = () => new Date().toISOString().split('T')[0];
// A gate pass number that opens the full gate pass detail pop-up when clicked — the same one the
// Gate Passes page uses. Stops the click reaching a clickable table row underneath.
const GatePassLink = ({ id, number, style }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" title="Click to view gate pass details"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', textDecoration: 'underline', textUnderlineOffset: 2, ...style }}>
        {number}
      </button>
      {open && <span onClick={(e) => e.stopPropagation()}><GatePassDetailModal gatePassId={id} onClose={() => setOpen(false)} /></span>}
    </>
  );
};

// ── main component ────────────────────────────────────────────────────────────

const BatchManagement = () => {
  const [batches, setBatches]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [selected, setSelected]     = useState(null);
  const [activeTab, setActiveTab]   = useState('amad');
  const [toast, setToast]           = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [products, setProducts]     = useState([]);
  const [customers, setCustomers]   = useState([]);
  const [vendors, setVendors]       = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadBatches = useCallback(async () => {
    try {
      setLoading(true);
      const res = await batchService.getAll();
      setBatches(res.data || []);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load reference data once on mount
  useEffect(() => {
    loadBatches();
    productService.getAll({ activeOnly: true })
      // Labor/service placeholder products (CONSUMABLE) are cost entries, not physical
      // commodities — a Batch always tracks a real shipment of goods.
      .then(r => setProducts((r.data || r || [])
        .filter(p => p.category !== 'CONSUMABLE')
        .map(p => ({ value: p.id, label: p.name, code: p.productCode }))))
      .catch(() => {});
    customerService.getByType('VENDOR')
      .then(r => setVendors((r.data || r || []).map(c => ({ value: c.id, label: c.name, code: c.customerCode }))))
      .catch(() => {});
    customerService.getAll()
      .then(r => setCustomers((r.data || r || [])
        .filter(c => c.customerType === 'CUSTOMER' || c.customerType === 'BOTH')
        .map(c => ({ value: c.id, label: c.name, code: c.customerCode }))))
      .catch(() => {});
    warehouseService.getAll(true)
      .then(r => setWarehouses((r.data || r || []).map(w => ({ value: w.name, label: w.name + (w.location ? ` — ${w.location}` : '') }))))
      .catch(() => {});
  }, [loadBatches]);

  const openBatch = (batch) => {
    setSelected(batch);
    setActiveTab('amad');
  };

  if (selected) {
    return (
      <BatchDetail
        batch={selected}
        onBack={() => { setSelected(null); loadBatches(); }}
        showToast={showToast}
        toast={toast}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        customers={customers}
        vendors={vendors}
        warehouses={warehouses}
      />
    );
  }

  return (
    <div className="financial-container">
      <Toast toast={toast} />

      <div className="financial-header">
        <div>
          <h2 className="financial-title">Batch Management</h2>
          <p className="financial-subtitle">
            Track mandi purchases, sorting, container costs, and export dispatch per batch
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <CsvActions filename="batches" headers={BATCH_CSV_HEADERS} matrix={buildBatchCsvRows(batches)} />
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New Batch
          </button>
        </div>
      </div>

      {showCreate && (
        <CreateBatchForm
          products={products}
          warehouses={warehouses}
          onCreated={(b, gatePass) => { setShowCreate(false); setBatches(prev => [b, ...prev]); showToast(`Batch created with gate pass ${gatePass?.gatePassNumber || ''}`.trim()); }}
          onClose={() => setShowCreate(false)}
        />
      )}

      {loading ? <Spinner /> : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                {['Batch Code','Product','Month','Godown','Status','Gate Passes In','Gate Passes Out','Weight (kg)','Money Spent','Money Earned',''].map(h => (
                  <th key={h} style={['Gate Passes In','Gate Passes Out','Weight (kg)','Money Spent','Money Earned'].includes(h) ? { textAlign: 'right' } : undefined}
                    title={{
                      'Gate Passes In': 'Number of gate passes for goods that came in (not cancelled)',
                      'Gate Passes Out': 'Number of gate passes for goods that went out (not cancelled)',
                      'Weight (kg)': 'Weight that came in, and (small text) weight that went out',
                      'Money Spent': 'Everything spent on this batch: goods bought, labor, bags, shipping',
                      'Money Earned': 'Total of the sales invoices sent for this batch',
                    }[h]}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {batches.length === 0 && (
                <tr><td colSpan={11} style={{ padding: 30, textAlign: 'center', color: 'var(--color-text-tertiary)' }}>No batches yet. Create your first batch to start tracking.</td></tr>
              )}
              {batches.map(b => (
                <tr key={b.id} style={{ cursor: 'pointer' }} onClick={() => openBatch(b)}>
                  <td style={{ fontWeight: 600 }}>{b.batchCode}</td>
                  <td>{b.productName}</td>
                  <td className="nowrap">{b.batchMonth ? b.batchMonth.substring(0, 7) : '—'}</td>
                  <td style={{ color: 'var(--color-text-secondary)' }}>{b.godownName || '—'}</td>
                  <td>{badge(b.status, STATUS_STYLE)}</td>
                  <td className="nowrap" style={{ textAlign: 'right' }}>{b.entryGatePasses ?? 0}</td>
                  <td className="nowrap" style={{ textAlign: 'right' }}>{b.exitGatePasses ?? 0}</td>
                  <td className="nowrap" style={{ textAlign: 'right' }} title={`In: ${kg(b.entryWeightKg ?? 0)} · Out: ${kg(b.exitWeightKg ?? 0)}`}>
                    <div>{kg(b.entryWeightKg ?? 0)}</div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>went out: {kg(b.exitWeightKg ?? 0)}</div>
                  </td>
                  <td className="nowrap" style={{ textAlign: 'right', fontWeight: 600 }}>{pkr(b.totalCost ?? 0)}</td>
                  <td className="nowrap" style={{ textAlign: 'right', fontWeight: 600, color: 'var(--color-success)' }}>{pkr(b.totalRevenue ?? 0)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="action-btn btn-secondary" onClick={(e) => { e.stopPropagation(); openBatch(b); }}>
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── Create Batch pop-up (batch + its mandatory first gate pass, saved together) ─────────

const CreateBatchForm = ({ products, warehouses, onCreated, onClose }) => {
  const initialMonth = today().substring(0, 7);
  const [form, setForm] = useState({
    batchCode: '', productId: '', productName: '', batchMonth: initialMonth + '-01', godownName: '', notes: ''
  });
  // The mandatory gate pass: same state/shape as the Gate Passes page form. 'NEW' marks "the
  // batch that is being created right now" so the form's existing product-lock logic applies.
  const [gp, setGp] = useState({ ...emptyForm(), batchId: 'NEW' });
  const [productCatalog, setProductCatalog] = useState([]);
  const [warehouseList, setWarehouseList] = useState([]);
  const [partyList, setPartyList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    productService.getAll({ activeOnly: true })
      .then(r => { const l = r?.data || r || []; setProductCatalog(Array.isArray(l) ? l : []); }).catch(() => {});
    warehouseService.getAll(true)
      .then(r => { const l = r?.data || r || []; setWarehouseList(Array.isArray(l) ? l : []); }).catch(() => {});
    customerService.getAll()
      .then(r => { const l = r?.data || r || []; setPartyList(Array.isArray(l) ? l : []); }).catch(() => {});
  }, []);

  // Auto-fill batch code when month changes (or on mount)
  const fetchSuggestedCode = useCallback(async (monthStr) => {
    try {
      const code = await batchService.suggestCode(monthStr);
      if (code) setForm(p => ({ ...p, batchCode: code }));
    } catch {}
  }, []);

  useEffect(() => { fetchSuggestedCode(initialMonth); }, [fetchSuggestedCode, initialMonth]);

  const handleProductChange = (e) => {
    const id = e.target.value;
    set('productId', id);
    if (!id) return;
    const found = products.find(p => String(p.value) === id);
    if (found) set('productName', found.label);
    // The gate pass carries the batch's product — fill and lock it, as the Gate Passes page
    // does when a batch is picked there.
    const prod = productCatalog.find(p => String(p.id) === id);
    setGp(g => ({
      ...g,
      items: [{
        ...(g.items[0] || emptyItem()),
        productId: Number(id),
        itemName: found?.label || prod?.name || '',
        itemCode: prod?.productCode || found?.code || '',
        unit: prod?.unit || 'KG',
        packSizeKg: parsePackSizeKg(prod?.packSize),
      }],
    }));
  };

  const handleMonthChange = (e) => {
    const m = e.target.value;
    set('batchMonth', m + '-01');
    fetchSuggestedCode(m);
  };

  const handleGodownChange = (e) => {
    set('godownName', e.target.value);
    setGp(g => ({ ...g, warehouseName: e.target.value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.productId) {
      setErr('Please select a product from the catalog — free text is not allowed to prevent duplicate entries.'); return;
    }
    if (!gp.partyName || !gp.partyName.trim()) {
      setErr('Mandatory Gate Pass: select the Supplier / Vendor the goods are coming from.'); return;
    }
    const itemError = validateGatePassItems(gp);
    if (itemError) { setErr(`Mandatory Gate Pass: ${itemError}`); return; }
    setLoading(true); setErr(null);
    try {
      const res = await batchService.createWithGatePass(
        { ...form, batchMonth: form.batchMonth.substring(0, 7) + '-01' },
        { ...buildGatePassPayload(gp), type: 'ENTRY' }
      );
      onCreated(res.data.batch, res.data.gatePass);
    } catch (e2) { setErr(e2.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={() => !loading && onClose()}>
      <div className="modal-content" style={{ maxWidth: '780px' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>New Batch</h3>
          <button type="button" className="modal-close" onClick={() => !loading && onClose()}>✕</button>
        </div>
        <form onSubmit={submit}>
          {err && <div style={{ background: 'var(--color-error-light)', color: 'var(--color-error)', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{err}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            <Input label="Batch Code" placeholder="Auto-generated (e.g. B042026-1)" value={form.batchCode} onChange={e => set('batchCode', e.target.value)} />
            {products.length > 0 ? (
              <SearchableSelect label="Product *" value={form.productId}
                options={products.map(o => typeof o === 'string' ? { value: o, label: o } : o)}
                onChange={(value) => handleProductChange({ target: { value: String(value) } })} />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', fontWeight: 500 }}>Product *</label>
                <div style={{ padding: '7px 10px', border: '1px solid var(--color-warning-border)', borderRadius: 6, fontSize: 13, background: 'var(--color-warning-light)', color: 'var(--color-warning-strong)' }}>
                  No products in catalog. Go to <strong>Product Catalog → + Add Product</strong> first.
                </div>
              </div>
            )}
            <Input label="Month" type="month" value={form.batchMonth.substring(0,7)} onChange={handleMonthChange} />
            {warehouses.length > 0
              ? <Select label="Godown / Warehouse" value={form.godownName} onChange={handleGodownChange} options={warehouses} />
              : <Input label="Godown / Warehouse" placeholder="e.g. Haji Mumtaz Godown" value={form.godownName} onChange={handleGodownChange} />
            }
            <Input label="Notes" value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>

          <div style={{ margin: '22px 0 12px', paddingTop: 16, borderTop: '2px solid var(--color-border-medium)' }}>
            <h3 style={{ margin: 0, fontSize: 15 }}>Mandatory Gate Pass <span style={{ color: 'var(--color-error)' }}>*</span></h3>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--color-text-secondary)' }}>
              A batch starts with goods coming in, so it cannot be created without at least one gate pass. It is saved as a Draft ENTRY and linked to this batch — Issue and Receive it from Gate Passes as usual.
            </p>
          </div>

          <GatePassFormFields
            batchMode
            form={gp} setForm={setGp}
            productCatalog={productCatalog} setProductCatalog={setProductCatalog}
            warehouseList={warehouseList} partyList={partyList}
          />

          <div className="modal-footer">
            <button type="button" className="btn-secondary" disabled={loading} onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Creating…' : 'Create Batch & Gate Pass'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── New Gate Pass pop-up opened from inside a batch (batch + product + godown pre-filled) ──

const NewGatePassForBatch = ({ batch, onCreated, onClose }) => {
  const [gp, setGp] = useState({
    ...emptyForm(),
    batchId: String(batch.id),
    warehouseName: batch.godownName || '',
  });
  const [productCatalog, setProductCatalog] = useState([]);
  const [warehouseList, setWarehouseList] = useState([]);
  const [partyList, setPartyList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    productService.getAll({ activeOnly: true }).then(r => {
      const l = r?.data || r || [];
      const list = Array.isArray(l) ? l : [];
      setProductCatalog(list);
      // The batch's product is locked in, exactly as when the batch is picked on the Gate Passes page.
      const prod = list.find(p => String(p.id) === String(batch.productId));
      if (batch.productId) {
        setGp(g => ({
          ...g,
          items: [{
            ...emptyItem(),
            productId: batch.productId,
            itemName: batch.productName || prod?.name || '',
            itemCode: prod?.productCode || '',
            unit: prod?.unit || 'KG',
            packSizeKg: parsePackSizeKg(prod?.packSize),
          }],
        }));
      }
    }).catch(() => {});
    warehouseService.getAll(true)
      .then(r => { const l = r?.data || r || []; setWarehouseList(Array.isArray(l) ? l : []); }).catch(() => {});
    customerService.getAll()
      .then(r => { const l = r?.data || r || []; setPartyList(Array.isArray(l) ? l : []); }).catch(() => {});
  }, [batch.productId, batch.productName]);

  const submit = async (e) => {
    e.preventDefault();
    const itemError = validateGatePassItems(gp);
    if (itemError) { setErr(itemError); return; }
    setLoading(true); setErr(null);
    try {
      const res = await gatePassService.create(buildGatePassPayload(gp));
      if (res.success) onCreated(res.data);
      else setErr(res.message || 'Failed to create gate pass');
    } catch (e2) { setErr(e2.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={() => !loading && onClose()}>
      <div className="modal-content" style={{ maxWidth: '780px' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>New Gate Pass · {batch.batchCode}</h3>
          <button type="button" className="modal-close" onClick={() => !loading && onClose()}>✕</button>
        </div>
        <form onSubmit={submit}>
          {err && <div style={{ background: 'var(--color-error-light)', color: 'var(--color-error)', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{err}</div>}
          <GatePassFormFields
            form={gp} setForm={setGp}
            productCatalog={productCatalog} setProductCatalog={setProductCatalog}
            warehouseList={warehouseList} partyList={partyList} batchList={[batch]}
          />
          <div className="modal-footer">
            <button type="button" className="btn-secondary" disabled={loading} onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Creating…' : 'Create Gate Pass'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Batch Detail ──────────────────────────────────────────────────────────────

const BatchDetail = ({ batch, onBack, showToast, toast, activeTab, setActiveTab, customers, vendors, warehouses }) => {
  const [pnl, setPnl]                   = useState(null);
  const [linkedDocs, setLinkedDocs]     = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [showNewGp, setShowNewGp]       = useState(false);
  const [refreshKey, setRefreshKey]     = useState(0);

  const loadPnl = useCallback(async () => {
    try { const r = await batchService.getPnl(batch.id); setPnl(r.data); } catch {}
  }, [batch.id]);

  const loadLinkedDocs = useCallback(async () => {
    try { const r = await batchService.getLinkedDocs(batch.id); setLinkedDocs(r.data); } catch {}
  }, [batch.id]);

  useEffect(() => {
    if (activeTab === 'pnl') loadPnl();
    if (activeTab === 'docs') loadLinkedDocs();
  }, [activeTab, loadPnl, loadLinkedDocs]);

  const updateStatus = async (s) => {
    setStatusLoading(true);
    try { await batchService.updateStatus(batch.id, s); showToast('Status updated'); batch.status = s; }
    catch (e) { showToast(e.message, 'error'); }
    finally { setStatusLoading(false); }
  };

  const tabs = [
    { key: 'amad',     label: 'Goods In (Sourcing)' },
    { key: 'grading',  label: 'Sorting & Waste' },
    { key: 'container',label: 'Processing Costs' },
    { key: 'tarseel',  label: 'Shipping Out' },
    { key: 'docs',     label: 'Documents' },
    { key: 'pnl',      label: 'Profit & Loss' },
  ];

  return (
    <div className="financial-container">
      {/* Toast was previously only rendered by the list view (BatchManagement's own return) —
          since opening a batch fully replaces that view (`if (selected) return <BatchDetail/>`),
          any showToast() called from in here (status change, grading, processing, etc.) set the
          state but had nothing mounted to display it — a real silent-failure bug, caught
          2026-09-23 when a batch-close error (expired session) produced no visible feedback at
          all. Fixed by rendering the same Toast here too. */}
      <Toast toast={toast} />
      {/* Header */}
      <div className="financial-header" style={{ alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
          <button onClick={onBack} className="btn-secondary" aria-label="Back to batches" style={{ padding: '0 12px' }}>
            ← Back
          </button>
          <div style={{ minWidth: 0 }}>
            <h2 className="financial-title">{batch.batchCode}</h2>
            <p className="financial-subtitle" style={{ marginTop: 2 }}>{batch.productName} · {batch.godownName || '—'} · {batch.batchMonth?.substring(0,7)}</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn-primary" onClick={() => setShowNewGp(true)}>+ New Gate Pass</button>
          {badge(batch.status, STATUS_STYLE)}
          <select value={batch.status} onChange={e => updateStatus(e.target.value)} disabled={statusLoading}
            className="form-control" style={{ width: 'auto', minHeight: 34, padding: '4px 10px', fontSize: 13 }} aria-label="Batch status">
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="tab-navigation" role="tablist">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} role="tab" aria-selected={activeTab === t.key}
            className={`tab-button ${activeTab === t.key ? 'active' : ''}`}>
            {t.label}
          </button>
        ))}
      </div>

      {showNewGp && (
        <NewGatePassForBatch
          batch={batch}
          onClose={() => setShowNewGp(false)}
          onCreated={(created) => {
            setShowNewGp(false);
            setRefreshKey(k => k + 1); // remount the open tab so it picks up the new gate pass
            if (activeTab === 'docs') loadLinkedDocs();
            showToast(`Gate pass ${created?.gatePassNumber || ''} created`.trim());
          }}
        />
      )}

      {activeTab === 'amad'      && <AmadTab      key={refreshKey} batchId={batch.id} showToast={showToast} warehouses={warehouses} batchCode={batch.batchCode} />}
      {activeTab === 'grading'   && <GradingTab   batchId={batch.id} showToast={showToast} productName={batch.productName} defaultWarehouse={batch.godownName} />}
      {activeTab === 'container' && <ContainerTab batchId={batch.id} showToast={showToast} customers={customers} />}
      {activeTab === 'tarseel'   && <TarseelTab   key={refreshKey} batchId={batch.id} showToast={showToast} customers={customers} batchCode={batch.batchCode} />}
      {activeTab === 'docs'      && <LinkedDocsTab docs={linkedDocs} reload={loadLinkedDocs} batchId={batch.id} />}
      {activeTab === 'pnl'       && <PnlTab       pnl={pnl} reload={loadPnl} />}
    </div>
  );
};

// ── Amad Tab ──────────────────────────────────────────────────────────────────

const AmadTab = ({ batchId, showToast, warehouses = [], batchCode = '' }) => {
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [entryPasses, setEntryPasses] = useState([]);
  const [importing, setImporting] = useState(false);
  const [delConfirmId, setDelConfirmId] = useState(null);
  const [editLabor, setEditLabor]   = useState(null); // { id, value }
  const [editingEntry, setEditingEntry] = useState(null); // full entry being edited
  const [editForm, setEditForm]     = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [createPoFor, setCreatePoFor] = useState(null); // entry to create PO from
  const [poSaving, setPoSaving]     = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await batchService.getAmad(batchId);
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) { showToast(e.message, 'error'); }
    finally { setLoading(false); }
  }, [batchId]);

  useEffect(() => {
    load();
    // Load ENTRY gate passes for import option
    gatePassService.getByType('ENTRY')
      .then(r => {
        const all = r.data || r || [];
        setEntryPasses(all.filter(gp =>
          gp.status === 'RECEIVED' &&
          (!gp.batchCode || gp.batchCode === batchCode)
        ));
      })
      .catch(() => {});
  }, [load, batchCode]);

  const importFromGP = async (gpId) => {
    if (!gpId) return;
    setImporting(true);
    try {
      await batchService.importAmadFromGatePass(batchId, gpId);
      showToast('Imported from gate pass');
      await load();
    } catch (e) { showToast(e.message, 'error'); }
    finally { setImporting(false); }
  };

  const del = (id) => setDelConfirmId(id);
  const doDelete = async () => {
    const id = delConfirmId; setDelConfirmId(null);
    try { await batchService.deleteAmad(batchId, id); showToast('Deleted'); load(); }
    catch (e) { showToast(e.message, 'error'); }
  };

  const startEdit = (r) => {
    setEditingEntry(r.id);
    setEditForm({
      entryDate: r.entryDate || today(),
      supplierName: r.supplierName || '',
      warehouseName: r.warehouseName || '',
      boriCount: r.boriCount != null ? String(r.boriCount) : '',
      weightKg: r.weightKg != null ? String(r.weightKg) : '',
      ratePerKg: r.ratePerKg != null ? String(r.ratePerKg) : (r.ratePer40Kg ? String(Number(r.ratePer40Kg) / 40) : ''),
      sourcingLabor: r.sourcingLabor != null ? String(r.sourcingLabor) : '0',
      notes: r.notes || '',
    });
  };
  const setEF = (k, v) => setEditForm(p => ({ ...p, [k]: v }));

  const saveEdit = async () => {
    setEditSaving(true);
    try {
      await batchService.patchAmad(batchId, editingEntry, editForm);
      showToast('Entry updated');
      setEditingEntry(null);
      await load();
    } catch (e) { showToast(e.message, 'error'); }
    finally { setEditSaving(false); }
  };

  const doCreatePo = async () => {
    const r = createPoFor;
    setPoSaving(true);
    try {
      const po = await purchaseService.create({
        vendorId: r.vendor?.id || null,
        purchaseDate: r.entryDate,
        currency: 'PKR',
        notes: `Auto-created from batch sourcing entry (${r.supplierName})`,
        updateStock: false,
        items: [{
          itemName: r.supplierName || 'Raw Material',
          quantity: r.weightKg,
          unit: 'KG',
          unitPrice: r.ratePerKg || (r.ratePer40Kg ? Number(r.ratePer40Kg) / 40 : 0),
        }],
      });
      // Link PO to entry permanently so button stays hidden after refresh
      await batchService.patchAmad(batchId, r.id, { linkedPurchaseId: po.id });
      showToast('Purchase Order created successfully!', 'success');
      setCreatePoFor(null);
      await load();
    } catch (e) { showToast(e.message, 'error'); }
    finally { setPoSaving(false); }
  };

  const totals = rows.reduce((a, r) => ({
    bori:   a.bori   + (Number(r.boriCount) || 0),
    weight: a.weight + (Number(r.weightKg)  || 0),
    amount: a.amount + (Number(r.totalAmount)|| 0),
  }), { bori: 0, weight: 0, amount: 0 });

  // Already-imported gate pass IDs
  const importedGpIds = new Set(rows.filter(r => r.gatePass).map(r => r.gatePass.id));
  const availableToImport = entryPasses.filter(gp => !importedGpIds.has(gp.id));

  return (
    <div>
      {/* Import from Gate Pass — primary action */}
      {availableToImport.length > 0 && (
        <div style={{ background: 'var(--color-warning-light)', border: '1px solid var(--color-warning-border)', borderRadius: 8, padding: '12px 16px', marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-warning-strong)', marginBottom: 8 }}>
            Gate passes waiting to be imported ({availableToImport.length})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {availableToImport.map(gp => (
              <button key={gp.id} onClick={() => importFromGP(gp.id)} disabled={importing}
                style={{ background: '#fff', border: '1px solid var(--color-warning-border)', borderRadius: 6, padding: '5px 14px', cursor: 'pointer', fontSize: 12, color: 'var(--color-warning-strong)', fontWeight: 500 }}>
                {importing ? '...' : `Import ${gp.gatePassNumber}`}
                <span style={{ color: 'var(--color-warning-strong)', marginLeft: 6 }}>{gp.partyName} · {gp.totalWeight ? Number(gp.totalWeight).toLocaleString() + ' kg' : ''}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <p style={{ color: 'var(--color-text-secondary)', fontSize: 13, marginTop: 0 }}>
        Every sourcing entry comes from a gate pass: it appears here automatically when an ENTRY gate pass linked to this batch is marked <strong>Received</strong>.
        Need to record a purchase? Use <strong>+ New Gate Pass</strong> above.
      </p>

      {loading ? <Spinner /> : (
        <div style={{ overflowX: 'auto', marginBottom: 16 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--color-neutral-50)', borderBottom: '2px solid var(--color-border-medium)' }}>
                {['Source','Date','Godown','Supplier','Container','Bags','Weight KG','Rate/KG','Receiving Labor','Total PKR',''].map(h => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: ['Source','Date','Godown','Supplier','Container'].includes(h) ? 'left' : 'right', color: 'var(--color-neutral-700)', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={11} style={{ padding: 24, textAlign: 'center', color: 'var(--color-text-tertiary)' }}>
                  No entries yet. Create an ENTRY gate pass for this batch and mark it Received.
                </td></tr>
              )}
              {rows.map(r => (
                <React.Fragment key={r.id}>
                <tr style={{ borderBottom: editingEntry === r.id ? 'none' : '1px solid var(--color-bg-tertiary)', background: editingEntry === r.id ? 'var(--color-warning-light)' : 'var(--color-bg-primary)' }}>
                  <td style={{ padding: '7px 10px' }}>
                    {r.gatePass
                      ? <GatePassLink id={r.gatePass.id} number={r.gatePass.gatePassNumber} style={{ background: 'var(--color-success-light)', color: 'var(--color-success)', padding: '1px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600 }} />
                      : <span style={{ background: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)', padding: '1px 8px', borderRadius: 999, fontSize: 11 }}>Manual</span>}
                  </td>
                  <td style={{ padding: '7px 10px' }}>{r.entryDate}</td>
                  <td style={{ padding: '7px 10px', color: 'var(--color-text-secondary)' }}>{r.warehouseName || '—'}</td>
                  <td style={{ padding: '7px 10px', fontWeight: 500 }}>
                    {r.vendor ? <span style={{ color: 'var(--color-primary)' }}>{r.supplierName}</span> : r.supplierName}
                  </td>
                  <td style={{ padding: '7px 10px', color: r.containerNumber ? 'var(--color-accent)' : 'var(--color-text-tertiary)' }}>
                    {/* Goods can arrive via container too (imports), not just leave via one — surfaces
                        the ENTRY gate pass's own container number, added 2026-09-14. */}
                    {r.containerNumber || '—'}
                  </td>
                  <td style={{ padding: '7px 10px', textAlign: 'right' }}>{r.boriCount ? Number(r.boriCount).toLocaleString() : '—'}</td>
                  <td style={{ padding: '7px 10px', textAlign: 'right' }}>{Number(r.weightKg).toLocaleString()}</td>
                  <td style={{ padding: '7px 10px', textAlign: 'right' }}>
                    {r.ratePerKg ? Number(r.ratePerKg).toFixed(3) : r.ratePer40Kg ? (Number(r.ratePer40Kg)/40).toFixed(3) : '—'}
                  </td>
                  <td style={{ padding: '7px 10px', textAlign: 'right' }}>
                    {/* Gate-pass-derived rows: Labor already lives on the Gate Pass itself — no
                        second editing surface for the same number here, just show it read-only.
                        Manual (no-gate-pass) rows still need their own inline editor since there's
                        no Gate Pass to set it from. */}
                    {r.gatePass ? (
                      <span style={{ color: Number(r.sourcingLabor) > 0 ? 'var(--color-neutral-700)' : 'var(--color-text-tertiary)' }} title="Set on the Gate Pass — edit it there">
                        {Number(r.sourcingLabor || 0).toLocaleString()}
                      </span>
                    ) : editLabor?.id === r.id ? (
                      <span style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', alignItems: 'center' }}>
                        <input type="number" value={editLabor.value} autoFocus
                          onChange={e => setEditLabor(p => ({ ...p, value: e.target.value }))}
                          style={{ width: 72, padding: '2px 6px', border: '1px solid var(--color-border-strong)', borderRadius: 4, fontSize: 12, textAlign: 'right' }} />
                        <button onClick={async () => {
                          try {
                            await batchService.patchAmad(batchId, r.id, { sourcingLabor: editLabor.value });
                            showToast('Labor updated'); setEditLabor(null); await load();
                          } catch (e) { showToast(e.message, 'error'); }
                        }} style={{ background: 'var(--color-success)', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 11 }}>✓</button>
                        <button onClick={() => setEditLabor(null)}
                          style={{ background: 'var(--color-border-medium)', border: 'none', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', fontSize: 11 }}>✕</button>
                      </span>
                    ) : (
                      <span style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                        <span style={{ color: Number(r.sourcingLabor) > 0 ? 'var(--color-neutral-700)' : 'var(--color-text-tertiary)' }}>
                          {Number(r.sourcingLabor || 0).toLocaleString()}
                        </span>
                        <button onClick={() => setEditLabor({ id: r.id, value: String(r.sourcingLabor || '0') })}
                          style={{ background: 'var(--color-info-light)', color: 'var(--color-primary)', border: 'none', borderRadius: 4, padding: '1px 7px', cursor: 'pointer', fontSize: 10, fontWeight: 500 }}>
                          + Labor
                        </button>
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600, color: 'var(--color-success)' }}>{Number(r.totalAmount || 0).toLocaleString()}</td>
                  <td style={{ padding: '7px 10px', textAlign: 'right' }}>
                    <span style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      {!r.gatePass && editingEntry !== r.id && (
                        <>
                          <button onClick={() => startEdit(r)}
                            style={{ background: 'var(--color-warning-light)', color: 'var(--color-warning-strong)', border: '1px solid var(--color-warning-border)', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 500 }}>
                            Edit
                          </button>
                          {r.linkedPurchaseId ? (
                            <span style={{ fontSize: 10, color: 'var(--color-success)', fontWeight: 600 }}>✓ PO Created</span>
                          ) : (
                            <button onClick={() => setCreatePoFor(r)}
                              style={{ background: 'var(--color-accent-light)', color: 'var(--color-accent-hover)', border: '1px solid var(--color-accent-light)', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 500 }}>
                              → PO
                            </button>
                          )}
                        </>
                      )}
                      {editingEntry === r.id && (
                        <button onClick={() => setEditingEntry(null)}
                          style={{ background: 'var(--color-border-medium)', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 11 }}>Cancel</button>
                      )}
                      <button onClick={() => del(r.id)} style={{ background: 'var(--color-error-light)', color: 'var(--color-error)', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 11 }}>✕</button>
                    </span>
                  </td>
                </tr>
                {editingEntry === r.id && (
                  <tr style={{ background: 'var(--color-warning-light)', borderBottom: '2px solid var(--color-warning-border)' }}>
                    <td colSpan={11} style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8 }}>
                        <Input label="Date" type="date" value={editForm.entryDate} onChange={e => setEF('entryDate', e.target.value)} />
                        <Input label="Supplier" value={editForm.supplierName} onChange={e => setEF('supplierName', e.target.value)} />
                        {warehouses.length > 0
                          ? <Select label="Godown" value={editForm.warehouseName} onChange={e => setEF('warehouseName', e.target.value)} options={warehouses} />
                          : <Input label="Godown" value={editForm.warehouseName} onChange={e => setEF('warehouseName', e.target.value)} />
                        }
                        <Input label="Bags" type="number" value={editForm.boriCount} onChange={e => setEF('boriCount', e.target.value)} />
                        <Input label="Weight KG" type="number" value={editForm.weightKg} onChange={e => setEF('weightKg', e.target.value)} />
                        <Input label="Rate/KG" type="number" value={editForm.ratePerKg} onChange={e => setEF('ratePerKg', e.target.value)} />
                        <Input label="Receiving Labor" type="number" value={editForm.sourcingLabor} onChange={e => setEF('sourcingLabor', e.target.value)} />
                        <Input label="Notes" value={editForm.notes} onChange={e => setEF('notes', e.target.value)} />
                      </div>
                      <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                        <button onClick={saveEdit} disabled={editSaving}
                          style={{ background: 'var(--color-info)', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 18px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                          {editSaving ? 'Saving…' : 'Save Changes'}
                        </button>
                        <button onClick={() => setEditingEntry(null)}
                          style={{ background: 'var(--color-border-medium)', border: 'none', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
              {rows.length > 0 && (
                <tr style={{ background: 'var(--color-neutral-50)', fontWeight: 700, borderTop: '2px solid var(--color-border-medium)' }}>
                  <td colSpan={5} style={{ padding: '8px 10px' }}>TOTAL ({rows.length} entries)</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{totals.bori.toLocaleString()}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{totals.weight.toLocaleString()} kg</td>
                  <td colSpan={2}></td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--color-success)' }}>PKR {totals.amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                  <td></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {delConfirmId && <ConfirmModal message="Delete this sourcing entry?" onConfirm={doDelete} onCancel={() => setDelConfirmId(null)} />}

      {createPoFor && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 420, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12, color: 'var(--color-text-primary)' }}>Create Purchase Order</div>
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>
              A PO will be created for this sourcing entry:
              <div style={{ background: 'var(--color-neutral-50)', borderRadius: 8, padding: '10px 14px', marginTop: 10, fontSize: 12 }}>
                <div><strong>Supplier:</strong> {createPoFor.supplierName || '—'}</div>
                <div><strong>Date:</strong> {createPoFor.entryDate}</div>
                <div><strong>Weight:</strong> {Number(createPoFor.weightKg).toLocaleString()} KG</div>
                <div><strong>Rate/KG:</strong> {createPoFor.ratePerKg ? Number(createPoFor.ratePerKg).toFixed(3) : (createPoFor.ratePer40Kg ? (Number(createPoFor.ratePer40Kg)/40).toFixed(3) : '—')}</div>
                <div><strong>Amount:</strong> PKR {Number(createPoFor.totalAmount || 0).toLocaleString()}</div>
                {!createPoFor.vendor && <div style={{ color: 'var(--color-warning)', marginTop: 6, fontSize: 11 }}>⚠ No vendor linked — PO will be created without a vendor ID</div>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setCreatePoFor(null)} disabled={poSaving}
                style={{ background: 'var(--color-border-medium)', border: 'none', borderRadius: 7, padding: '8px 18px', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={doCreatePo} disabled={poSaving || !createPoFor?.vendor}
                title={!createPoFor?.vendor ? 'No vendor linked to this entry — cannot create PO' : ''}
                style={{ background: createPoFor?.vendor ? 'var(--color-accent-hover)' : 'var(--color-text-tertiary)', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 20px', cursor: createPoFor?.vendor ? 'pointer' : 'not-allowed', fontWeight: 600, fontSize: 13 }}>
                {poSaving ? 'Creating…' : 'Create PO'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Grading Tab ───────────────────────────────────────────────────────────────

const GradingTab = ({ batchId, showToast, productName, defaultWarehouse }) => {
  const [rows, setRows]   = useState([]);
  const [loading, setLoading] = useState(true);
  const emptyForm = { entryType: 'GRADED_RECOVERY', entryDate: today(), productId: '', grade: '', bagCount: '', weightKg: '', wastageReason: '', estimatedLossAmount: '', notes: '' };
  const [form, setForm]   = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [delConfirmId, setDelConfirmId] = useState(null);
  const [grades, setGrades] = useState([]);          // the waste grades (Chaant, Goli...)
  const [showNewGrade, setShowNewGrade] = useState(false);
  const [addingStandard, setAddingStandard] = useState(false);

  const load = useCallback(async () => {
    try { setLoading(true); const r = await batchService.getGrading(batchId); setRows(r.data || []); }
    catch {} finally { setLoading(false); }
  }, [batchId]);

  const loadGrades = useCallback(async () => {
    try {
      const r = await productService.getAll({ category: 'SELLABLE_WASTE', activeOnly: true });
      const list = r?.data || r || [];
      setGrades(Array.isArray(list) ? list.filter(isWasteGrade) : []);
    } catch { /* the form just shows no grades yet */ }
  }, []);

  useEffect(() => { load(); loadGrades(); }, [load, loadGrades]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const isWastage = form.entryType === 'WASTAGE_LOSS';

  const addStandardGrades = async () => {
    setAddingStandard(true);
    try {
      for (const name of STANDARD_WASTE_GRADES) {
        if (!grades.some(g => g.name.toLowerCase() === name.toLowerCase())) await createWasteGrade(name);
      }
      await loadGrades();
      showToast('Standard waste grades added');
    } catch (e) { showToast(e.message, 'error'); }
    finally { setAddingStandard(false); }
  };

  const save = async () => {
    if (isWastage) {
      if (!form.wastageReason || !form.weightKg) { showToast('Reason and weight are required', 'error'); return; }
    } else if (!form.productId || !form.weightKg) {
      showToast('Pick the waste grade and enter the weight in kg', 'error'); return;
    }
    setSaving(true);
    try {
      await batchService.addGrading(batchId, form);
      showToast(isWastage ? 'Unsellable waste recorded — stock and books updated' : 'Sorted out — moved into the waste grade\'s stock');
      setForm(p => ({ ...emptyForm, entryType: p.entryType }));
      load(); loadGrades();
    } catch (e) { showToast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const del = (id) => setDelConfirmId(id);
  const doDelete = async () => {
    const id = delConfirmId; setDelConfirmId(null);
    try { await batchService.deleteGrading(batchId, id); load(); loadGrades(); showToast('Deleted'); }
    catch (e) { showToast(e.message, 'error'); }
  };

  const totals = rows.reduce((a, r) => {
    const kgv = parseFloat(r.weightKg || 0);
    const lost = r.entryType === 'WASTAGE_LOSS';
    return {
      bags: a.bags + (r.bagCount || 0),
      kg: a.kg + kgv,
      sellableKg: a.sellableKg + (lost ? 0 : kgv),
      unsellableKg: a.unsellableKg + (lost ? kgv : 0),
      loss: a.loss + parseFloat(r.estimatedLossAmount || 0),
    };
  }, { bags: 0, kg: 0, sellableKg: 0, unsellableKg: 0, loss: 0 });

  return (
    <div>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: 13, marginTop: 0 }}>
        Everything that leaves this batch other than the good goods you ship. <strong>Sellable waste</strong> is
        sorted out by grade, kept as stock, and sold later with a Sellable Waste gate pass + invoice.
        <strong> Unsellable waste</strong> is gone for good — its cost is written off. Either way the batch's
        kg and rupees always add up.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={() => set('entryType', 'GRADED_RECOVERY')}
          style={{ flex: 1, minWidth: 220, padding: '10px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13,
            border: !isWastage ? '2px solid var(--color-warning)' : '1px solid var(--color-border-strong)',
            background: !isWastage ? 'var(--color-warning-light)' : '#fff', color: !isWastage ? 'var(--color-warning)' : 'var(--color-text-secondary)' }}>
          Sellable Waste <span style={{ fontWeight: 400 }}>— sorted out by grade, sold later</span>
        </button>
        <button onClick={() => set('entryType', 'WASTAGE_LOSS')}
          style={{ flex: 1, minWidth: 220, padding: '10px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13,
            border: isWastage ? '2px solid var(--color-error)' : '1px solid var(--color-border-strong)',
            background: isWastage ? 'var(--color-error-light)' : '#fff', color: isWastage ? 'var(--color-error)' : 'var(--color-text-secondary)' }}>
          Unsellable Waste <span style={{ fontWeight: 400 }}>— gone for good</span>
        </button>
      </div>

      {!isWastage ? (
        <div style={{ background: 'var(--color-warning-light)', border: '1px solid var(--color-warning-border)', borderRadius: 10, padding: 16, marginBottom: 20 }}>
          <h4 style={{ margin: '0 0 4px', fontSize: 14, color: 'var(--color-warning)' }}>+ Sort out sellable waste</h4>
          <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--color-warning-strong)' }}>
            {productName ? `This weight moves out of ${productName} and into the grade's stock` : "This weight moves out of the batch's stock and into the grade's stock"}
            {defaultWarehouse ? ` at ${defaultWarehouse}` : ''}, carrying its cost with it.
          </p>
          {grades.length === 0 && (
            <div style={{ background: 'var(--color-bg-primary)', border: '1px dashed var(--color-warning-border)', borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 13 }}>
              No waste grades yet. <button onClick={addStandardGrades} disabled={addingStandard}
                style={{ marginLeft: 6, background: 'var(--color-warning)', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                {addingStandard ? 'Adding…' : 'Add the standard grades (Chaant, Jamboo, Goli, Safi)'}
              </button> or add your own below.
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
            <Input label="Date" type="date" value={form.entryDate} onChange={e => set('entryDate', e.target.value)} />
            <div>
              <SearchableSelect label="Waste grade *" value={form.productId} placeholder="— Select grade —"
                options={grades.map(g => ({ value: g.id, label: `${g.name} · in stock ${Number(g.currentStock || 0).toLocaleString()} kg` }))}
                onChange={(value) => {
                  const g = grades.find(x => String(x.id) === String(value));
                  setForm(p => ({ ...p, productId: value, grade: g ? g.name : '' }));
                }} />
              <button type="button" onClick={() => setShowNewGrade(true)}
                style={{ marginTop: 4, background: 'none', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', fontSize: 12, padding: 0, textDecoration: 'underline' }}>
                + New grade
              </button>
            </div>
            <Input label="Bags / Units" type="number" value={form.bagCount} onChange={e => set('bagCount', e.target.value)} />
            <Input label="Weight KG *" type="number" value={form.weightKg} onChange={e => set('weightKg', e.target.value)}
              hint="This exact weight is what becomes sellable stock" />
          </div>
          <Input label="Notes" value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Optional details..." />
          <button onClick={save} disabled={saving}
            style={{ marginTop: 12, background: 'var(--color-warning)', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 20px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
            {saving ? 'Saving…' : 'Sort Out'}
          </button>
        </div>
      ) : (
        <div style={{ background: 'var(--color-error-light)', border: '1px solid var(--color-error-border)', borderRadius: 10, padding: 16, marginBottom: 20 }}>
          <h4 style={{ margin: '0 0 4px', fontSize: 14, color: 'var(--color-error)' }}>+ Record Unsellable Waste</h4>
          <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--color-error-strong)' }}>
            {productName ? `Stock will be deducted automatically from ${productName}` : 'Stock will be deducted automatically'}
            {defaultWarehouse ? ` at ${defaultWarehouse}.` : '.'}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
            <Input label="Date" type="date" value={form.entryDate} onChange={e => set('entryDate', e.target.value)} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', fontWeight: 500 }}>Reason *</label>
              <select value={form.wastageReason} onChange={e => set('wastageReason', e.target.value)}
                style={{ padding: '7px 10px', border: '1px solid var(--color-border-strong)', borderRadius: 6, fontSize: 13 }}>
                <option value="">Select reason...</option>
                {WASTAGE_REASONS.map(r => <option key={r} value={r}>{fmtReason(r)}</option>)}
              </select>
            </div>
            <Input label="Bags / Units" type="number" value={form.bagCount} onChange={e => set('bagCount', e.target.value)} />
            <Input label="Weight KG *" type="number" value={form.weightKg} onChange={e => set('weightKg', e.target.value)} />
            <Input label="Value lost (PKR)" type="number" value={form.estimatedLossAmount} onChange={e => set('estimatedLossAmount', e.target.value)}
              hint="Leave blank — worked out as weight × this batch's average cost per kg" />
          </div>
          <Input label="Notes" value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Optional details..." />
          <button onClick={save} disabled={saving}
            style={{ marginTop: 12, background: 'var(--color-error)', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 20px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
            {saving ? 'Saving…' : 'Record Unsellable Waste'}
          </button>
        </div>
      )}

      {loading ? <Spinner /> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--color-neutral-50)', borderBottom: '2px solid var(--color-border-medium)' }}>
                {['Date','Type','Grade / Reason','Bags','Weight KG','Value lost (PKR)',''].map(h => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: ['Date','Type','Grade / Reason'].includes(h)?'left':'right', color: 'var(--color-neutral-700)', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const loss = r.entryType === 'WASTAGE_LOSS';
                return (
                  <tr key={r.id} style={{ borderBottom: '1px solid var(--color-bg-tertiary)' }}>
                    <td style={{ padding: '7px 10px' }}>{r.entryDate}</td>
                    <td style={{ padding: '7px 10px' }}>
                      <span style={{ fontSize: 11 }}>{loss ? 'Unsellable' : 'Sellable'}</span>
                    </td>
                    <td style={{ padding: '7px 10px' }}>
                      <span style={{ background: loss ? 'var(--color-error-light)' : 'var(--color-warning-light)', color: loss ? 'var(--color-error)' : 'var(--color-warning-strong)', padding: '2px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 600 }}>{r.grade}</span>
                    </td>
                    <td style={{ padding: '7px 10px', textAlign: 'right' }}>{r.bagCount}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right' }}>{r.weightKg != null ? Number(r.weightKg).toLocaleString() : '—'}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600, color: 'var(--color-error)' }}>
                      {loss && r.estimatedLossAmount != null ? `-${Number(r.estimatedLossAmount).toLocaleString()}` : '—'}
                    </td>
                    <td><button onClick={() => del(r.id)} style={{ background: 'var(--color-error-light)', color: 'var(--color-error)', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 11 }}>✕</button></td>
                  </tr>
                );
              })}
              {rows.length > 0 && (
                <tr style={{ background: 'var(--color-neutral-50)', fontWeight: 700, borderTop: '2px solid var(--color-border-medium)' }}>
                  <td colSpan={3} style={{ padding: '8px 10px' }}>TOTAL</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{totals.bags.toLocaleString()}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{totals.kg.toLocaleString()}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--color-error)' }}>
                    -{totals.loss.toLocaleString(undefined,{maximumFractionDigits:0})}
                  </td>
                  <td></td>
                </tr>
              )}
            </tbody>
          </table>
          {rows.length === 0 && <p style={{ textAlign: 'center', color: 'var(--color-text-tertiary)', padding: 20 }}>Nothing sorted out or lost yet.</p>}
          {rows.length > 0 && (
            <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 10 }}>
              Sellable waste: <strong>{totals.sellableKg.toLocaleString()} kg</strong> (sold through Gate Passes → Invoices) ·
              Unsellable waste: <strong>{totals.unsellableKg.toLocaleString()} kg</strong>, cost written off PKR <strong>{totals.loss.toLocaleString(undefined,{maximumFractionDigits:0})}</strong>
            </p>
          )}
        </div>
      )}
      {showNewGrade && (
        <WasteGradeModal existing={grades} onClose={() => setShowNewGrade(false)}
          onCreated={(g) => { setShowNewGrade(false); setGrades(prev => [...prev, g]); setForm(p => ({ ...p, productId: g.id, grade: g.name })); showToast(`Waste grade "${g.name}" added`); }} />
      )}
      {delConfirmId && <ConfirmModal message="Delete this grading entry?" onConfirm={doDelete} onCancel={() => setDelConfirmId(null)} />}
    </div>
  );
};

// ── Container Cost Tab ────────────────────────────────────────────────────────

const ContainerTab = ({ batchId, showToast, customers }) => {
  const [rows, setRows]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm]   = useState({
    entryDate: today(), operationType: 'LOADING', containerNumber: '', customerId: '',
    buyerCode: '', buyerName: '', bagCount: '', bardanaType: '',
    bardanaRatePerBag: '', weightKg: '', laborRatePerKg: '', flatCost: '0', notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [delConfirmId, setDelConfirmId] = useState(null);

  const load = useCallback(async () => {
    try { setLoading(true); const r = await batchService.getContainerCosts(batchId); setRows(r.data || []); }
    catch {} finally { setLoading(false); }
  }, [batchId]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleCustomerChange = (e) => {
    const id = e.target.value;
    set('customerId', id);
    if (id) {
      const found = customers.find(c => String(c.value) === id);
      if (found) set('buyerName', found.label);
    } else {
      set('buyerName', '');
    }
  };

  const isLoading       = form.operationType === 'LOADING';
  const isPackagingOnly = form.operationType === 'PACKAGING_ONLY';
  const showBagsSection = isLoading || isPackagingOnly;
  const previewLabor   = form.weightKg && form.laborRatePerKg && form.operationType === 'LOADING'
    ? (parseFloat(form.weightKg) * parseFloat(form.laborRatePerKg)).toFixed(0) : form.flatCost || '0';
  const previewBardana = form.bagCount && form.bardanaRatePerBag
    ? (parseFloat(form.bagCount) * parseFloat(form.bardanaRatePerBag)).toFixed(0) : '0';

  const save = async () => {
    setSaving(true);
    try {
      await batchService.addContainerCost(batchId, form);
      showToast('Added');
      setForm(p => ({ ...p, containerNumber: '', customerId: '', buyerCode: '', buyerName: '', bagCount: '', weightKg: '', notes: '' }));
      load();
    } catch (e) { showToast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const del = (id) => setDelConfirmId(id);
  const doDelete = async () => {
    const id = delConfirmId; setDelConfirmId(null);
    try { await batchService.deleteContainerCost(batchId, id); load(); showToast('Deleted'); }
    catch (e) { showToast(e.message, 'error'); }
  };

  const totals = rows.reduce((a,r) => ({ labor: a.labor+parseFloat(r.totalLaborCost||0), bardana: a.bardana+parseFloat(r.totalBardanaCost||0) }), { labor:0, bardana:0 });

  return (
    <div>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: 13, marginTop: 0 }}>
        Enter processing, labor and packaging costs per container. Use non-Loading ops for flat-rate charges (shifting, repackaging, misc).
      </p>
      <div style={{ background: 'var(--color-success-light)', border: '1px solid var(--color-success-border)', borderRadius: 10, padding: 16, marginBottom: 20 }}>
        <h4 style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--color-success)' }}>+ Add Processing Cost</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
          <Input label="Date" type="date" value={form.entryDate} onChange={e => set('entryDate', e.target.value)} />
          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
            <label style={{ fontSize:12, color:'var(--color-text-secondary)', fontWeight:500 }}>Operation Type</label>
            <select value={form.operationType} onChange={e => set('operationType', e.target.value)}
              style={{ padding:'7px 10px', border:'1px solid var(--color-border-strong)', borderRadius:6, fontSize:13, background:'#fff' }}>
              {OP_TYPES.map(t => <option key={t} value={t}>{OP_LABEL[t] || t}</option>)}
            </select>
          </div>
          <Input label="Container No" value={form.containerNumber} onChange={e => set('containerNumber', e.target.value)} placeholder="MSCU1234567" />
          {customers.length > 0
            ? <SearchableSelect label="Buyer" value={form.customerId}
                options={customers.map(o => typeof o === 'string' ? { value: o, label: o } : o)}
                onChange={(value) => handleCustomerChange({ target: { value: String(value) } })} />
            : <Input label="Buyer Name" value={form.buyerName} onChange={e => set('buyerName', e.target.value)} placeholder="Buyer name" />
          }
          {/* Packaging fields — shown for LOADING and PACKAGING_ONLY */}
          {showBagsSection && <>
            <Input label="Bags / Units" type="number" value={form.bagCount} onChange={e => set('bagCount', e.target.value)} />
            <LockedSelect label="Packaging Type" options={BARDANA_OPTIONS} value={form.bardanaType}
              onChange={v => set('bardanaType', v)} placeholder="Type packaging type..." />
            <Input label="Packaging Rate / Bag" type="number" value={form.bardanaRatePerBag} onChange={e => set('bardanaRatePerBag', e.target.value)} placeholder="cost per bag" />
          </>}
          {/* Labor fields — only for LOADING */}
          {isLoading && <>
            <Input label="Weight KG" type="number" value={form.weightKg} onChange={e => set('weightKg', e.target.value)} />
            <Input label="Processing Labor Rate / KG" type="number" value={form.laborRatePerKg} onChange={e => set('laborRatePerKg', e.target.value)} />
          </>}
          {/* Flat cost — for all other op types */}
          {!isLoading && !isPackagingOnly && <Input label="Flat Cost (PKR)" type="number" value={form.flatCost} onChange={e => set('flatCost', e.target.value)} />}
          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
            <span style={{ fontSize:11, color:'var(--color-text-secondary)' }}>Preview</span>
            <span style={{ fontSize:12, color:'var(--color-success)' }}>Processing Labor: PKR {Number(previewLabor).toLocaleString()}</span>
            <span style={{ fontSize:12, color:'var(--color-success)' }}>Packaging: PKR {Number(previewBardana).toLocaleString()}</span>
          </div>
        </div>
        <button onClick={save} disabled={saving}
          style={{ marginTop: 12, background: 'var(--color-success)', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 20px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
          {saving ? 'Saving…' : 'Add'}
        </button>
      </div>
      {loading ? <Spinner /> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--color-neutral-50)', borderBottom: '2px solid var(--color-border-medium)' }}>
                {['Date','Type','Container','Buyer','Bags','Packaging','Wt KG','Processing Labor PKR','Packaging PKR','Total PKR',''].map(h => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: ['Date','Type','Container','Buyer'].includes(h)?'left':'right', color: 'var(--color-neutral-700)', fontWeight: 600, whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid var(--color-bg-tertiary)', background: !r.containerNumber ? 'var(--color-neutral-50)' : '' }}>
                  <td style={{ padding: '7px 10px' }}>{r.entryDate}</td>
                  <td style={{ padding: '7px 10px' }}><span style={{ fontSize:11, background:'var(--color-info-light)', color:'var(--color-info)', padding:'1px 6px', borderRadius:9999 }}>{OP_LABEL[r.operationType] || fmt(r.operationType)}</span></td>
                  <td style={{ padding: '7px 10px', fontFamily: 'var(--font-mono)' }}>{r.containerNumber || <span style={{color:'var(--color-text-tertiary)'}}>internal</span>}</td>
                  <td style={{ padding: '7px 10px' }}>
                    {r.customer ? <span style={{ color: 'var(--color-primary)' }}>{r.buyerName}</span> : (r.buyerName || '—')}
                  </td>
                  <td style={{ padding: '7px 10px', textAlign:'right' }}>{r.bagCount?.toLocaleString() || '—'}</td>
                  <td style={{ padding: '7px 10px', textAlign:'right', color:'var(--color-text-secondary)' }}>{r.bardanaType || '—'}</td>
                  <td style={{ padding: '7px 10px', textAlign:'right' }}>{Number(r.weightKg||0).toLocaleString()}</td>
                  <td style={{ padding: '7px 10px', textAlign:'right' }}>{Number(r.totalLaborCost||0).toLocaleString()}</td>
                  <td style={{ padding: '7px 10px', textAlign:'right' }}>{Number(r.totalBardanaCost||0).toLocaleString()}</td>
                  <td style={{ padding: '7px 10px', textAlign:'right', fontWeight:600, color:'var(--color-success)' }}>{(Number(r.totalLaborCost||0)+Number(r.totalBardanaCost||0)).toLocaleString()}</td>
                  <td><button onClick={() => del(r.id)} style={{ background:'var(--color-error-light)', color:'var(--color-error)', border:'none', borderRadius:4, padding:'2px 8px', cursor:'pointer', fontSize:11 }}>✕</button></td>
                </tr>
              ))}
              {rows.length > 0 && (
                <tr style={{ background:'var(--color-neutral-50)', fontWeight:700, borderTop:'2px solid var(--color-border-medium)' }}>
                  <td colSpan={7} style={{ padding:'8px 10px' }}>TOTAL</td>
                  <td style={{ padding:'8px 10px', textAlign:'right', color:'var(--color-success)' }}>PKR {totals.labor.toLocaleString(undefined,{maximumFractionDigits:0})}</td>
                  <td style={{ padding:'8px 10px', textAlign:'right', color:'var(--color-success)' }}>PKR {totals.bardana.toLocaleString(undefined,{maximumFractionDigits:0})}</td>
                  <td style={{ padding:'8px 10px', textAlign:'right', color:'var(--color-success)' }}>PKR {(totals.labor+totals.bardana).toLocaleString(undefined,{maximumFractionDigits:0})}</td>
                  <td></td>
                </tr>
              )}
            </tbody>
          </table>
          {rows.length === 0 && <p style={{ textAlign:'center', color:'var(--color-text-tertiary)', padding:20 }}>No container costs yet.</p>}
        </div>
      )}
      {delConfirmId && <ConfirmModal message="Delete this processing entry?" onConfirm={doDelete} onCancel={() => setDelConfirmId(null)} />}
    </div>
  );
};

// ── Tarseel Tab ───────────────────────────────────────────────────────────────

const TarseelTab = ({ batchId, showToast, customers, batchCode }) => {
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [gatePasses, setGatePasses] = useState([]);
  const [invoices, setInvoices]     = useState([]);
  const [gds, setGds]               = useState([]);
  const [batchSummary, setBatchSummary] = useState(null);
  const [weightWarn, setWeightWarn]     = useState('');
  const [form, setForm] = useState({
    dispatchDate: today(), containerNumber: '', customerId: '', buyerCode: '', buyerName: '',
    weightKg: '', bagCount: '', bardanaType: '', destinationPort: '', destinationCountry: '',
    gatePassId: '', invoiceId: '', gdId: '', invoiceNumber: '', notes: ''
  });
  const [saving, setSaving]             = useState(false);
  const [delConfirmId, setDelConfirmId] = useState(null);
  const [noInvoiceConfirm, setNoInvoiceConfirm] = useState(false);

  const load = useCallback(async () => {
    try { setLoading(true); const r = await batchService.getTarseel(batchId); setRows(r.data || []); }
    catch {} finally { setLoading(false); }
  }, [batchId]);

  // Load linkable documents — gate passes (EXIT), invoices (EXPORT), GDs + batch weight summary
  useEffect(() => {
    load();
    batchService.getPnl(batchId)
      .then(r => setBatchSummary(r.data || r))
      .catch(() => {});
    gatePassService.getByType('EXIT')
      .then(r => setGatePasses((r.data || r || [])
        .filter(gp => !gp.batchCode || gp.batchCode === batchCode)
        .map(gp => ({
          value: gp.id,
          label: `${gp.gatePassNumber} – ${gp.partyName}${gp.containerNumber ? ' · '+gp.containerNumber : ''}${gp.batchCode ? ' ✓' : ''}`,
          containerNumber: gp.containerNumber || '',
          totalWeight: gp.totalWeight || '',
          totalQuantity: gp.totalQuantity || '',
          partyName: gp.partyName || '',
          gatePassDate: gp.gatePassDate || '',
          gatePassNumber: gp.gatePassNumber || '',
        }))))
      .catch(() => {});
    invoiceService.getAll()
      .then(r => setInvoices((r.data || r || [])
        .filter(inv => inv.status !== 'CANCELLED' && (!inv.batchCode || inv.batchCode === batchCode))
        .map(inv => ({
          value: inv.id,
          salesType: inv.salesType || 'LOCAL',
          label: `[${inv.salesType || 'LOCAL'}] ${inv.invoiceNumber} – ${inv.customer?.name || inv.customerName || ''}${inv.batchCode ? ' ✓' : ''}`
        }))))
      .catch(() => {});
    gdService.getAll()
      .then(r => setGds((r.data || r || [])
        .filter(gd => !gd.batchCode || gd.batchCode === batchCode)
        .map(gd => ({
          value: gd.id,
          label: `${gd.gdNumber} – ${gd.consigneeName}${gd.batchCode ? ' ✓' : ''}`
        }))))
      .catch(() => {});
  }, [load]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleCustomerChange = (e) => {
    const id = e.target.value;
    set('customerId', id);
    if (id) {
      const found = customers.find(c => String(c.value) === id);
      if (found) set('buyerName', found.label);
    } else {
      set('buyerName', '');
    }
  };

  const handleInvoiceChange = (e) => {
    const id = e.target.value;
    set('invoiceId', id);
    if (id) {
      const found = invoices.find(i => String(i.value) === id);
      if (found) {
        const num = found.label.split(' – ')[0];
        set('invoiceNumber', num);
      }
    }
  };

  const handleGatePassChange = (e) => {
    const id = e.target.value;
    set('gatePassId', id);
    setWeightWarn('');
    if (id) {
      const gp = gatePasses.find(g => String(g.value) === id);
      if (gp) {
        if (gp.containerNumber) set('containerNumber', gp.containerNumber);
        if (gp.totalWeight && parseFloat(gp.totalWeight) > 0) {
          set('weightKg', String(gp.totalWeight));
        }
        if (gp.totalQuantity && parseFloat(gp.totalQuantity) > 0) set('bagCount', String(Math.round(parseFloat(gp.totalQuantity))));
        if (gp.gatePassDate) set('dispatchDate', gp.gatePassDate);
      }
    }
  };

  const checkWeightMismatch = (dispatchKg) => {
    if (!form.gatePassId) return;
    const gp = gatePasses.find(g => String(g.value) === form.gatePassId);
    if (gp && gp.totalWeight && parseFloat(gp.totalWeight) > 0) {
      const diff = Math.abs(parseFloat(dispatchKg) - parseFloat(gp.totalWeight));
      if (diff > 1) {
        setWeightWarn(`Gate pass weight is ${Number(gp.totalWeight).toLocaleString()} kg — dispatch weight differs by ${diff.toLocaleString()} kg`);
      } else {
        setWeightWarn('');
      }
    }
  };

  const fillFromGatePass = (gp) => {
    setForm(p => ({
      ...p,
      gatePassId: String(gp.value),
      containerNumber: gp.containerNumber || p.containerNumber,
      weightKg: gp.totalWeight && parseFloat(gp.totalWeight) > 0 ? String(gp.totalWeight) : p.weightKg,
      bagCount: gp.totalQuantity && parseFloat(gp.totalQuantity) > 0 ? String(Math.round(parseFloat(gp.totalQuantity))) : p.bagCount,
      dispatchDate: gp.gatePassDate || p.dispatchDate,
    }));
  };

  const doSave = async () => {
    setNoInvoiceConfirm(false);
    setSaving(true);
    try {
      await batchService.addTarseel(batchId, form);
      showToast('Container dispatched');
      setForm(p => ({ ...p, containerNumber:'', customerId:'', buyerCode:'', buyerName:'', weightKg:'', bagCount:'', gatePassId:'', invoiceId:'', gdId:'', invoiceNumber:'', notes:'' }));
      setWeightWarn('');
      load();
      batchService.getPnl(batchId).then(r => setBatchSummary(r.data || r)).catch(() => {});
    } catch (e) { showToast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const save = async () => {
    if (!form.containerNumber || (!form.customerId && !form.buyerName) || !form.weightKg) {
      showToast('Container, buyer and weight are required', 'error'); return;
    }
    if (!form.gatePassId) {
      const linkedInv = invoices.find(i => String(i.value) === String(form.invoiceId));
      if (!linkedInv || linkedInv.salesType === 'EXPORT') {
        showToast('EXIT gate pass is required for EXPORT dispatch — create one in Gate Passes first', 'error'); return;
      }
      // LOCAL sale: gate pass optional, no block
    }
    if (!form.invoiceId && !form.invoiceNumber) {
      setNoInvoiceConfirm(true); return;
    }
    await doSave();
  };

  const del = (id) => setDelConfirmId(id);
  const doDelete = async () => {
    const id = delConfirmId; setDelConfirmId(null);
    try { await batchService.deleteTarseel(batchId, id); load(); showToast('Deleted'); }
    catch (e) { showToast(e.message, 'error'); }
  };

  const totals = rows.reduce((a,r) => ({ weight: a.weight+parseFloat(r.weightKg||0), bags: a.bags+(r.bagCount||0) }), { weight:0, bags:0 });

  const byBuyer = rows.reduce((m, r) => {
    const k = r.buyerName;
    if (!m[k]) m[k] = { name: k, containers: 0, weightKg: 0 };
    m[k].containers++;
    m[k].weightKg += parseFloat(r.weightKg || 0);
    return m;
  }, {});

  return (
    <div>
      <p style={{ color:'var(--color-text-secondary)', fontSize:13, marginTop:0 }}>
        Container/dispatch rows appear here automatically when an EXIT gate pass linked to this batch
        is dispatched via Invoice creation — every real dispatch already requires an Invoice (and, for
        Export, a Gate Pass too), so there's nothing to fill in manually here.
      </p>

      {Object.keys(byBuyer).length > 0 && (
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:16 }}>
          {Object.values(byBuyer).map(b => (
            <div key={b.name} style={{ background:'var(--color-info-light)', border:'1px solid var(--color-info-border)', borderRadius:8, padding:'10px 16px', fontSize:13 }}>
              <div style={{ fontWeight:700, color:'var(--color-primary)' }}>{b.name}</div>
              <div style={{ color:'var(--color-text-secondary)', fontSize:12 }}>{b.containers} container{b.containers!==1?'s':''} · {b.weightKg.toLocaleString()} kg</div>
            </div>
          ))}
        </div>
      )}

      {loading ? <Spinner /> : (
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
            <thead>
              <tr style={{ background:'var(--color-neutral-50)', borderBottom:'2px solid var(--color-border-medium)' }}>
                {['Date','Container','Buyer','Weight KG','Bags','Destination','Invoice','GD','GP',''].map(h => (
                  <th key={h} style={{ padding:'8px 10px', textAlign:['Weight KG','Bags'].includes(h)?'right':'left', color:'var(--color-neutral-700)', fontWeight:600, whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ borderBottom:'1px solid var(--color-bg-tertiary)', background: (!r.invoice && !r.invoiceNumber) ? 'var(--color-warning-light)' : undefined }}>
                  <td style={{ padding:'7px 10px' }}>{r.dispatchDate}</td>
                  <td style={{ padding:'7px 10px', fontFamily: 'var(--font-mono)', fontWeight:600 }}>{r.containerNumber}</td>
                  <td style={{ padding:'7px 10px' }}>
                    {r.customer ? <span style={{ color:'var(--color-primary)' }}>{r.buyerName}</span> : r.buyerName}
                  </td>
                  <td style={{ padding:'7px 10px', textAlign:'right', fontWeight:600 }}>{Number(r.weightKg).toLocaleString()}</td>
                  <td style={{ padding:'7px 10px', textAlign:'right' }}>{r.bagCount?.toLocaleString() || '—'}</td>
                  <td style={{ padding:'7px 10px', color:'var(--color-text-secondary)' }}>{r.destinationPort ? `${r.destinationPort}, ${r.destinationCountry}` : '—'}</td>
                  <td style={{ padding:'7px 10px' }}>
                    {r.invoice
                      ? <span style={{ color:'var(--color-primary)', fontSize:11, fontWeight:600 }}>{r.invoiceNumber || r.invoice?.invoiceNumber}</span>
                      : r.invoiceNumber
                        ? <span style={{ color:'var(--color-warning)', fontSize:11 }}>{r.invoiceNumber}</span>
                        : <span style={{ background:'var(--color-error-light)', color:'var(--color-error)', fontSize:10, fontWeight:600, padding:'1px 6px', borderRadius:4 }}>NO INVOICE</span>}
                  </td>
                  <td style={{ padding:'7px 10px' }}>
                    {r.goodsDeclaration
                      ? <span style={{ color:'var(--color-success)', fontSize:11, fontWeight:600 }}>{r.goodsDeclaration?.gdNumber}</span>
                      : <span style={{ color:'var(--color-text-tertiary)', fontSize:11 }}>—</span>}
                  </td>
                  <td style={{ padding:'7px 10px' }}>
                    {r.gatePass
                      ? <GatePassLink id={r.gatePass.id} number={r.gatePass.gatePassNumber} style={{ color:'var(--color-accent-hover)', fontSize:11, fontWeight:600 }} />
                      : <span style={{ color:'var(--color-text-tertiary)', fontSize:11 }}>—</span>}
                  </td>
                  <td><button onClick={() => del(r.id)} style={{ background:'var(--color-error-light)', color:'var(--color-error)', border:'none', borderRadius:4, padding:'2px 8px', cursor:'pointer', fontSize:11 }}>✕</button></td>
                </tr>
              ))}
              {rows.length > 0 && (
                <tr style={{ background:'var(--color-neutral-50)', fontWeight:700, borderTop:'2px solid var(--color-border-medium)' }}>
                  <td colSpan={3} style={{ padding:'8px 10px' }}>TOTAL ({rows.length} containers)</td>
                  <td style={{ padding:'8px 10px', textAlign:'right' }}>{totals.weight.toLocaleString()} kg</td>
                  <td style={{ padding:'8px 10px', textAlign:'right' }}>{totals.bags.toLocaleString()}</td>
                  <td colSpan={5}></td>
                </tr>
              )}
            </tbody>
          </table>
          {rows.length === 0 && <p style={{ textAlign:'center', color:'var(--color-text-tertiary)', padding:20 }}>No dispatches yet.</p>}
        </div>
      )}
      {delConfirmId && <ConfirmModal message="Delete this dispatch entry?" onConfirm={doDelete} onCancel={() => setDelConfirmId(null)} />}

      {noInvoiceConfirm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'#fff', borderRadius:12, padding:28, maxWidth:420, width:'90%', boxShadow:'0 8px 32px rgba(0,0,0,0.18)' }}>
            <div className="notice-mark notice-mark-warning" aria-hidden="true">!</div>
            <h3 style={{ margin:'0 0 10px', fontSize:16, color:'var(--color-warning-strong)' }}>No Invoice Linked</h3>
            <p style={{ margin:'0 0 20px', fontSize:13, color:'var(--color-text-secondary)', lineHeight:1.5 }}>
              This dispatch has no invoice. Every dispatch should have a matching sale invoice.
              You can proceed now and link the invoice after, but do not forget.
            </p>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button onClick={() => setNoInvoiceConfirm(false)}
                style={{ padding:'7px 18px', border:'1px solid var(--color-border-strong)', borderRadius:7, background:'#fff', cursor:'pointer', fontSize:13 }}>
                Cancel
              </button>
              <button onClick={doSave} disabled={saving}
                style={{ padding:'7px 18px', border:'none', borderRadius:7, background:'var(--color-warning)', color:'#fff', cursor:'pointer', fontWeight:600, fontSize:13 }}>
                {saving ? 'Saving…' : 'Dispatch Anyway'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Linked Docs Tab ───────────────────────────────────────────────────────────

const INV_STATUS_COLOR = { DRAFT:'var(--color-bg-tertiary)', SENT:'var(--color-info-light)', PAID:'var(--color-success-light)', PARTIAL:'var(--color-warning-light)', OVERDUE:'var(--color-error-light)', CANCELLED:'var(--color-bg-tertiary)' };
const GD_STATUS_COLOR  = { FILED:'var(--color-bg-tertiary)', DOCUMENTS_SUBMITTED:'var(--color-info-light)', SETTLEMENT_PENDING:'var(--color-warning-light)', SETTLED:'var(--color-success-light)', CANCELLED:'var(--color-bg-tertiary)' };
const GP_STATUS_COLOR  = { DRAFT:'var(--color-bg-tertiary)', ISSUED:'var(--color-info-light)', RECEIVED:'var(--color-success-light)', DISPATCHED:'var(--color-info-light)', CANCELLED:'var(--color-error-light)' };

const LinkedDocsTab = ({ docs, reload, batchId }) => {
  useEffect(() => { if (!docs) reload(); }, []);

  if (!docs) return <Spinner />;

  const { gatePasses = [], invoices = [], goodsDeclarations = [] } = docs;

  const Section = ({ title, color, children }) => (
    <div style={{ background:'#fff', border:'1px solid var(--color-border-medium)', borderRadius:10, overflow:'hidden', marginBottom:16 }}>
      <div style={{ background: color, padding:'10px 16px', fontWeight:600, fontSize:14, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <span>{title}</span>
        <span style={{ fontSize:12, fontWeight:400, color:'var(--color-text-secondary)' }}>{children.props.count} linked</span>
      </div>
      {children}
    </div>
  );

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <p style={{ margin:0, color:'var(--color-text-secondary)', fontSize:13 }}>
          Documents automatically linked when Tarseel entries reference them.
        </p>
        <button onClick={reload} style={{ background:'var(--color-bg-tertiary)', border:'none', borderRadius:6, padding:'6px 12px', cursor:'pointer', fontSize:12 }}>🔄 Refresh</button>
      </div>

      {/* Invoices */}
      <div style={{ background:'#fff', border:'1px solid var(--color-border-medium)', borderRadius:10, overflow:'hidden', marginBottom:16 }}>
        <div style={{ background:'var(--color-info-light)', padding:'10px 16px', fontWeight:600, fontSize:14, display:'flex', justifyContent:'space-between' }}>
          <span>Invoices</span>
          <span style={{ fontSize:12, color:'var(--color-text-secondary)' }}>{invoices.length} linked</span>
        </div>
        {invoices.length === 0
          ? <p style={{ padding:16, color:'var(--color-text-tertiary)', margin:0, fontSize:13 }}>No invoices linked yet. Link via Tarseel tab.</p>
          : <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead><tr style={{ borderBottom:'1px solid var(--color-border-medium)' }}>
                {['Invoice No','Customer','Date','Amount','Status'].map(h => <th key={h} style={{ padding:'8px 16px', textAlign:h==='Amount'?'right':'left', color:'var(--color-neutral-700)', fontWeight:600 }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {invoices.map(inv => (
                  <tr key={inv.id} style={{ borderBottom:'1px solid var(--color-bg-tertiary)' }}>
                    <td style={{ padding:'8px 16px', fontWeight:600, color:'var(--color-primary)' }}>{inv.invoiceNumber}</td>
                    <td style={{ padding:'8px 16px' }}>{inv.customerName || '—'}</td>
                    <td style={{ padding:'8px 16px', color:'var(--color-text-secondary)' }}>{inv.invoiceDate}</td>
                    <td style={{ padding:'8px 16px', textAlign:'right', fontWeight:600 }}>{inv.currency} {Number(inv.totalAmount||0).toLocaleString()}</td>
                    <td style={{ padding:'8px 16px' }}>
                      <span style={{ background: INV_STATUS_COLOR[inv.status]||'var(--color-bg-tertiary)', padding:'2px 8px', borderRadius:9999, fontSize:11, fontWeight:600 }}>{inv.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
        }
      </div>

      {/* GDs */}
      <div style={{ background:'#fff', border:'1px solid var(--color-border-medium)', borderRadius:10, overflow:'hidden', marginBottom:16 }}>
        <div style={{ background:'var(--color-success-light)', padding:'10px 16px', fontWeight:600, fontSize:14, display:'flex', justifyContent:'space-between' }}>
          <span>Goods Declarations</span>
          <span style={{ fontSize:12, color:'var(--color-text-secondary)' }}>{goodsDeclarations.length} linked</span>
        </div>
        {goodsDeclarations.length === 0
          ? <p style={{ padding:16, color:'var(--color-text-tertiary)', margin:0, fontSize:13 }}>No GDs linked yet.</p>
          : <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead><tr style={{ borderBottom:'1px solid var(--color-border-medium)' }}>
                {['GD Number','Filed','Invoice USD','Net PKR','Settlement','Status'].map(h => <th key={h} style={{ padding:'8px 16px', textAlign:['Invoice USD','Net PKR'].includes(h)?'right':'left', color:'var(--color-neutral-700)', fontWeight:600 }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {goodsDeclarations.map(gd => (
                  <tr key={gd.id} style={{ borderBottom:'1px solid var(--color-bg-tertiary)' }}>
                    <td style={{ padding:'8px 16px', fontWeight:600, color:'var(--color-success)' }}>{gd.gdNumber}</td>
                    <td style={{ padding:'8px 16px', color:'var(--color-text-secondary)' }}>{gd.filingDate}</td>
                    <td style={{ padding:'8px 16px', textAlign:'right' }}>{gd.invoiceValue ? usd(gd.invoiceValue) : '—'}</td>
                    <td style={{ padding:'8px 16px', textAlign:'right', fontWeight:600 }}>{gd.netPkrReceived ? pkr(gd.netPkrReceived) : '—'}</td>
                    <td style={{ padding:'8px 16px', color:'var(--color-text-secondary)' }}>{gd.settlementDate || '—'}</td>
                    <td style={{ padding:'8px 16px' }}>
                      <span style={{ background: GD_STATUS_COLOR[gd.status]||'var(--color-bg-tertiary)', padding:'2px 8px', borderRadius:9999, fontSize:11, fontWeight:600 }}>{fmt(gd.status)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
        }
      </div>

      {/* Gate Passes */}
      <div style={{ background:'#fff', border:'1px solid var(--color-border-medium)', borderRadius:10, overflow:'hidden' }}>
        <div style={{ background:'var(--color-accent-light)', padding:'10px 16px', fontWeight:600, fontSize:14, display:'flex', justifyContent:'space-between' }}>
          <span>Gate Passes</span>
          <span style={{ fontSize:12, color:'var(--color-text-secondary)' }}>{gatePasses.length} linked</span>
        </div>
        {gatePasses.length === 0
          ? <p style={{ padding:16, color:'var(--color-text-tertiary)', margin:0, fontSize:13 }}>No gate passes linked yet.</p>
          : <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead><tr style={{ borderBottom:'1px solid var(--color-border-medium)' }}>
                {['GP Number','Type','Party','Date','Status'].map(h => <th key={h} style={{ padding:'8px 16px', textAlign:'left', color:'var(--color-neutral-700)', fontWeight:600 }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {gatePasses.map(gp => (
                  <tr key={gp.id} style={{ borderBottom:'1px solid var(--color-bg-tertiary)' }}>
                    <td style={{ padding:'8px 16px', fontWeight:600 }}><GatePassLink id={gp.id} number={gp.gatePassNumber} style={{ color:'var(--color-accent-hover)', fontWeight:600 }} /></td>
                    <td style={{ padding:'8px 16px' }}>{gp.type}</td>
                    <td style={{ padding:'8px 16px' }}>{gp.partyName}</td>
                    <td style={{ padding:'8px 16px', color:'var(--color-text-secondary)' }}>{gp.gatePassDate}</td>
                    <td style={{ padding:'8px 16px' }}>
                      <span style={{ background: GP_STATUS_COLOR[gp.status]||'var(--color-bg-tertiary)', padding:'2px 8px', borderRadius:9999, fontSize:11, fontWeight:600 }}>{gp.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
        }
      </div>

      {/* Batch-level documents — quality certs, lab reports, photos. Separate from the
          auto-linked records above, which come from Tarseel references, not uploads. */}
      <div style={{ background:'#fff', border:'1px solid var(--color-border-medium)', borderRadius:10, padding:16, marginTop:16 }}>
        <div style={{ fontWeight:600, fontSize:14, marginBottom:4 }}>Batch Documents</div>
        <p style={{ margin:'0 0 4px', color:'var(--color-text-secondary)', fontSize:13 }}>Quality certificates, lab reports, photos — attached directly to this batch.</p>
        <AttachmentPanel entityType="BATCH" entityId={batchId} />
      </div>
    </div>
  );
};

// ── P&L Summary Tab ───────────────────────────────────────────────────────────

const PnlTab = ({ pnl, reload }) => {
  useEffect(() => { reload(); }, []);

  if (!pnl) return <Spinner />;

  const costPerKg      = Number(pnl.costPerKgExported || 0);
  const revenuePerKg   = Number(pnl.revenuePerKg || 0);
  const marginPerKg    = Number(pnl.marginPerKg || 0);
  const hasRevenue     = pnl.revenueSettled === true;
  const hasLocalSales  = Number(pnl.localInvoiceTotal || 0) > 0;

  // Only real status colours (good / bad) are kept; everything else reads in plain text colour so the
  // eye goes to what matters instead of a rainbow of values.
  const STATUS_COLORS = ['var(--color-success)', 'var(--color-error)'];
  const row = (label, value, color) => (
    <tr style={{ borderBottom:'1px solid var(--color-bg-tertiary)' }}>
      <td style={{ padding:'10px 16px', color:'var(--color-neutral-700)' }}>{label}</td>
      <td style={{ padding:'10px 16px', textAlign:'right', fontWeight:600, whiteSpace:'nowrap', color: STATUS_COLORS.includes(color) ? color : 'var(--color-text-primary)' }}>{value}</td>
    </tr>
  );

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <h3 style={{ margin:0, fontSize:16 }}>Profit &amp; Loss of this Batch</h3>
        <button onClick={reload} style={{ background:'var(--color-bg-tertiary)', border:'none', borderRadius:6, padding:'6px 12px', cursor:'pointer', fontSize:12 }}>Refresh</button>
      </div>

      <PnlDashboard pnl={pnl} hasRevenue={hasRevenue} />

      {/* The old row of summary cards was removed: every figure in it (goods bought, sorted out,
          shipped, cost per kg, local sales, GD settlement, sales, profit) is now either in the
          dashboard above or in the tables below, so it was only repeating itself. */}

      <h4 style={{ margin:'4px 0 12px', fontSize:12, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--color-text-tertiary)' }}>The detail</h4>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(340px, 1fr))', gap:16, marginBottom:16, alignItems:'start' }}>
        {/* Cost breakdown */}
        <div style={{ background:'#fff', border:'1px solid var(--color-border-medium)', borderRadius:10, overflow:'hidden' }}>
          <div style={{ background:'var(--color-neutral-50)', padding:'10px 16px', borderBottom:'1px solid var(--color-border-medium)', fontWeight:600, fontSize:14 }}>Money Spent</div>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <tbody>
              {row('Goods bought (Amad)', pkr(pnl.amadTotal))}
              {row('Labor (loading, sorting)', pkr(pnl.laborTotal))}
              {row('Packing bags (Bardana)', pkr(pnl.bardanaTotal))}
              {Number(pnl.shipmentCostTotal || 0) > 0 && row('Shipping (freight + clearing)', pkr(pnl.shipmentCostTotal))}
              <tr style={{ background:'var(--color-neutral-50)', borderTop:'2px solid var(--color-border-medium)' }}>
                <td style={{ padding:'10px 16px', fontWeight:700 }}>Total Money Spent</td>
                <td style={{ padding:'10px 16px', textAlign:'right', fontWeight:700, color:'var(--color-text-primary)', fontSize:15 }}>{pkr(pnl.totalCost)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Revenue / margin (shown once any invoice linked to this batch has been sent — no longer tied to GD settlement) */}
        <div style={{ background:'#fff', border:'1px solid var(--color-border-medium)', borderRadius:10, overflow:'hidden' }}>
          <div style={{ background:'var(--color-neutral-50)', padding:'10px 16px', borderBottom:'1px solid var(--color-border-medium)', fontWeight:600, fontSize:14 }}>
            {hasRevenue ? 'Sales & Profit' : 'Bags Check'}
          </div>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <tbody>
              {hasRevenue ? <>
                {Number(pnl.settledRevenuePkr || 0) > 0 && row('Export paperwork settled (GD) — for reference, not part of Total Sales', pkr(pnl.settledRevenuePkr), 'var(--color-info)')}
                {Number(pnl.invoiceValueUsd || 0) > 0 && row('Export invoice value (USD)', usd(pnl.invoiceValueUsd))}
                {hasLocalSales && row('Local sales billed', pkr(pnl.localInvoiceTotal), 'var(--color-accent-hover)')}
                {hasLocalSales && row('Local sales money received', pkr(pnl.localRevenuePkr), 'var(--color-accent-hover)')}
                {row('Total Sales (all invoices sent)', pkr(pnl.totalRevenuePkr), 'var(--color-success)')}
                {row('Total Money Spent', pkr(pnl.totalCost), 'var(--color-warning)')}
                <tr style={{ background: Number(pnl.profitPkr) >= 0 ? 'var(--color-success-light)' : 'var(--color-error-light)', borderTop:'2px solid var(--color-border-medium)' }}>
                  <td style={{ padding:'10px 16px', fontWeight:700 }}>Profit (Sales − Money Spent)</td>
                  <td style={{ padding:'10px 16px', textAlign:'right', fontWeight:700, color: Number(pnl.profitPkr) >= 0 ? 'var(--color-success)' : 'var(--color-error)', fontSize:15 }}>{pkr(pnl.profitPkr)}</td>
                </tr>
                {row('Sales per kg shipped', `PKR ${revenuePerKg.toFixed(2)}`, 'var(--color-info)')}
                {row('Cost per kg shipped', `PKR ${costPerKg.toFixed(2)}`)}
                {row('Profit per kg shipped', `PKR ${marginPerKg.toFixed(2)}`, Number(pnl.marginPerKg) >= 0 ? 'var(--color-success)' : 'var(--color-error)')}
              </> : <>
                {row('Bags bought (Amad)', (pnl.amadBags||0).toLocaleString())}
                {row('Less: sorted out as waste', `– ${(pnl.deductBags||0).toLocaleString()} bags`, 'var(--color-warning)')}
                {row('Bags left to ship', (pnl.netBags||0).toLocaleString())}
                {row('Bags shipped (Tarseel)', (pnl.exportBags||0).toLocaleString(), 'var(--color-success)')}
                {(pnl.deductBags||0) === 0 && (pnl.exportBags||0) === 0
                  ? row('Missing / unaccounted', 'Nothing shipped yet', 'var(--color-text-tertiary)')
                  : row('Missing / unaccounted', `${Number(pnl.shrinkageKg||0).toLocaleString()} kg`, 'var(--color-text-secondary)')}
              </>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Where every kg and rupee went: good output, sellable waste, unsellable waste */}
      <div style={{ background:'#fff', border:'1px solid var(--color-border-medium)', borderRadius:10, overflow:'hidden', marginBottom:16 }}>
        <div style={{ background:'var(--color-neutral-50)', padding:'10px 16px', borderBottom:'1px solid var(--color-border-medium)', fontWeight:600, fontSize:14 }}>Where Every Kilo Went (and the Waste Money)</div>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <tbody>
            {row('Goods bought', kg(pnl.amadWeightKg))}
            {row('Good goods shipped', `– ${kg(pnl.exportWeightKg)}`, 'var(--color-success)')}
            {row('Waste that can be sold', `– ${kg(pnl.sellableWasteKg)}`, 'var(--color-warning)')}
            {row('Waste that cannot be sold (thrown away)', `– ${kg(pnl.unsellableWasteKg)}`, 'var(--color-error)')}
            {row('Missing / unaccounted for', kg(pnl.shrinkageKg), Math.abs(Number(pnl.shrinkageKg || 0)) < 1 ? 'var(--color-success)' : 'var(--color-text-secondary)')}
            <tr><td colSpan={2} style={{ padding:0, borderTop:'2px solid var(--color-border-medium)' }} /></tr>
            {row('Money earned from selling waste', pkr(pnl.sellableWasteRevenuePkr), 'var(--color-info)')}
            {row('Money lost on thrown-away waste (already part of Money Spent)', pkr(pnl.wastageLossPkr), 'var(--color-error)')}
            {row('Cost per kg of good goods, after waste sales', `PKR ${Number(pnl.netCostPerGoodKg || 0).toFixed(2)}`, 'var(--color-text-primary)')}
          </tbody>
        </table>
      </div>

      {/* Grading breakdown */}
      {pnl.gradeSummary?.length > 0 && (
        <div style={{ background:'#fff', border:'1px solid var(--color-border-medium)', borderRadius:10, overflow:'hidden', marginBottom:16 }}>
          <div style={{ background:'var(--color-neutral-50)', padding:'10px 16px', borderBottom:'1px solid var(--color-border-medium)', fontWeight:600, fontSize:14 }}>Waste by Grade</div>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead><tr style={{ borderBottom:'1px solid var(--color-border-medium)' }}>
              {['Grade','Bags','Weight KG','Old estimate (PKR)','Money lost (PKR)'].map(h => <th key={h} style={{ padding:'8px 16px', textAlign:h==='Grade'?'left':'right', color:'var(--color-neutral-700)', fontWeight:600 }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {pnl.gradeSummary.map(g => (
                <tr key={g.grade} style={{ borderBottom:'1px solid var(--color-bg-tertiary)' }}>
                  <td style={{ padding:'8px 16px', fontWeight:600 }}>{g.grade}</td>
                  <td style={{ padding:'8px 16px', textAlign:'right' }}>{Number(g.bags).toLocaleString()}</td>
                  <td style={{ padding:'8px 16px', textAlign:'right' }}>{Number(g.weightKg).toLocaleString()}</td>
                  <td style={{ padding:'8px 16px', textAlign:'right' }}>{Number(g.recovery) > 0 ? pkr(g.recovery) : '—'}</td>
                  <td style={{ padding:'8px 16px', textAlign:'right', color: Number(g.loss) > 0 ? 'var(--color-error)' : undefined }}>{Number(g.loss) > 0 ? pkr(g.loss) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Buyer summary */}
      {pnl.buyerSummary?.length > 0 && (
        <div style={{ background:'#fff', border:'1px solid var(--color-border-medium)', borderRadius:10, overflow:'hidden' }}>
          <div style={{ background:'var(--color-neutral-50)', padding:'10px 16px', borderBottom:'1px solid var(--color-border-medium)', fontWeight:600, fontSize:14 }}>Exported by Buyer</div>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead><tr style={{ borderBottom:'1px solid var(--color-border-medium)' }}>
              {['Buyer','Weight KG','Bags','Cost @ Rate','Revenue / Buyer'].map(h => <th key={h} style={{ padding:'8px 16px', textAlign:['Weight KG','Bags','Cost @ Rate','Revenue / Buyer'].includes(h)?'right':'left', color:'var(--color-neutral-700)', fontWeight:600 }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {pnl.buyerSummary.map(b => {
                const buyerCost = Number(b.weightKg) * costPerKg;
                const buyerRev  = hasRevenue ? Number(b.weightKg) * revenuePerKg : null;
                return (
                  <tr key={b.buyerName} style={{ borderBottom:'1px solid var(--color-bg-tertiary)' }}>
                    <td style={{ padding:'8px 16px', fontWeight:600 }}>{b.buyerName}</td>
                    <td style={{ padding:'8px 16px', textAlign:'right' }}>{Number(b.weightKg).toLocaleString()}</td>
                    <td style={{ padding:'8px 16px', textAlign:'right' }}>{Number(b.bags).toLocaleString()}</td>
                    <td style={{ padding:'8px 16px', textAlign:'right', color:'var(--color-accent-hover)' }}>{pkr(buyerCost)}</td>
                    <td style={{ padding:'8px 16px', textAlign:'right', color: buyerRev ? 'var(--color-info)' : 'var(--color-text-tertiary)' }}>
                      {buyerRev ? pkr(buyerRev) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── P&L dashboard (visual summary shown above the detailed tables) ────────────────────────

const dashCard = { background:'var(--color-bg-primary)', border:'1px solid var(--color-border-medium)', borderRadius:12, padding:'16px 18px' };
const dashTitle = { fontSize:12, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', color:'var(--color-text-tertiary)', marginBottom:12 };

// Same colour language as the main dashboard: graphite = sales, ochre = money spent. Only profit/loss
// uses a status colour. `swatch` marks which of the two a figure belongs to.
const Kpi = ({ label, value, sub, valueColor, swatch }) => (
  <div style={{ ...dashCard, display:'flex', flexDirection:'column', gap:4 }}>
    <div style={{ display:'flex', alignItems:'center', gap:7, fontSize:13, color:'var(--color-text-secondary)', fontWeight:500 }}>
      {swatch && <i style={{ width:10, height:10, borderRadius:3, background:swatch, flexShrink:0 }} />}{label}
    </div>
    <div style={{ fontSize:26, fontWeight:600, letterSpacing:'-0.025em', color: valueColor || 'var(--color-text-primary)', lineHeight:1.15, fontVariantNumeric:'tabular-nums' }}>{value}</div>
    {sub && <div style={{ fontSize:12.5, color:'var(--color-text-tertiary)', lineHeight:1.4 }}>{sub}</div>}
  </div>
);

// Horizontal bar: label, proportional fill, value
const BarRow = ({ label, value, max, text, color }) => {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return (
    <div style={{ marginBottom:11 }}>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:4, gap:8 }}>
        <span style={{ color:'var(--color-neutral-700)', fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{label}</span>
        <span style={{ fontWeight:600, color:'var(--color-text-primary)', whiteSpace:'nowrap' }}>{text}</span>
      </div>
      <div style={{ height:8, borderRadius:99, background:'var(--color-bg-tertiary)', overflow:'hidden' }}>
        <div style={{ width:`${pct}%`, height:'100%', borderRadius:99, background:color, transition:'width .5s ease' }} />
      </div>
    </div>
  );
};

const PnlDashboard = ({ pnl, hasRevenue }) => {
  const n = (v) => Number(v || 0);
  const revenue = n(pnl.totalRevenuePkr), cost = n(pnl.totalCost), profit = n(pnl.profitPkr);
  const marginPct = revenue > 0 ? (profit / revenue) * 100 : 0;
  const profitTone = profit >= 0 ? 'var(--color-success)' : 'var(--color-error)';

  // Cost composition (recovery is a credit, so it is shown as a note rather than a slice)
  const slices = [
    { label:'Goods bought', value:n(pnl.amadTotal),         color:'var(--color-primary)' },
    { label:'Labor',        value:n(pnl.laborTotal),        color:'var(--color-accent)' },
    { label:'Packing bags', value:n(pnl.bardanaTotal),      color:'var(--color-info)' },
    { label:'Shipping',     value:n(pnl.shipmentCostTotal), color:'var(--color-warning)' },
  ].filter(s => s.value > 0);
  const sliceTotal = slices.reduce((a, s) => a + s.value, 0);
  const R = 52, C = 2 * Math.PI * R;
  let offset = 0;

  // Weight flow
  const bought = n(pnl.amadWeightKg);
  const shipped = n(pnl.exportWeightKg);
  const shrink = n(pnl.shrinkageKg);
  const yieldPct = bought > 0 ? (shipped / bought) * 100 : 0;

  const buyers = (pnl.buyerSummary || []).slice().sort((a, b) => n(b.weightKg) - n(a.weightKg)).slice(0, 5);
  const buyerMax = buyers.length ? n(buyers[0].weightKg) : 0;

  return (
    <div style={{ marginBottom:22 }}>
      {/* KPI strip */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(210px, 1fr))', gap:12, marginBottom:12 }}>
        <Kpi swatch="var(--color-primary)" label="Money earned (sales)" value={pkr(revenue)}
          sub={hasRevenue ? (n(pnl.sellableWasteRevenuePkr) > 0 ? `Includes ${pkr(pnl.sellableWasteRevenuePkr)} from selling waste` : 'Total of the invoices sent') : 'No sales invoice sent yet'} />
        <Kpi swatch="var(--color-accent)" label="Money spent" value={pkr(cost)}
          sub={`PKR ${n(pnl.costPerKgExported).toFixed(2)} for every kg shipped`} />
        <Kpi label={profit >= 0 ? 'Profit' : 'Loss'} valueColor={revenue > 0 || cost > 0 ? profitTone : undefined} value={pkr(Math.abs(profit))}
          sub={revenue <= 0 ? 'Shows after the first sale'
            : profit >= 0 ? `${marginPct.toFixed(1)}% of sales`
            : `Sales cover ${(revenue / Math.max(cost, 1) * 100).toFixed(revenue / Math.max(cost, 1) < 0.1 ? 1 : 0)}% of the money spent${shipped <= 0 ? ' · nothing shipped yet' : ''}`} />
        <Kpi label="Good goods shipped" value={kg(shipped)}
          sub={bought > 0 ? `${yieldPct.toFixed(0)}% of the ${kg(bought)} bought` : 'Nothing bought yet'} />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(300px, 1fr))', gap:12 }}>
        {/* Cost composition donut */}
        <div style={dashCard}>
          <div style={dashTitle}>Where the money was spent</div>
          {sliceTotal === 0 ? (
            <div style={{ fontSize:13, color:'var(--color-text-tertiary)', padding:'24px 0', textAlign:'center' }}>No spending recorded yet</div>
          ) : (
            <div style={{ display:'flex', alignItems:'center', gap:18, flexWrap:'wrap' }}>
              <svg width="132" height="132" viewBox="0 0 132 132" role="img" aria-label="Cost breakdown">
                <circle cx="66" cy="66" r={R} fill="none" stroke="var(--color-bg-tertiary)" strokeWidth="16" />
                {slices.map(s => {
                  const len = (s.value / sliceTotal) * C;
                  const el = (
                    <circle key={s.label} cx="66" cy="66" r={R} fill="none" stroke={s.color} strokeWidth="16"
                      strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-offset} transform="rotate(-90 66 66)" />
                  );
                  offset += len;
                  return el;
                })}
                <text x="66" y="62" textAnchor="middle" style={{ fontSize:10, fill:'var(--color-text-tertiary)' }}>Total spent</text>
                <text x="66" y="78" textAnchor="middle" style={{ fontSize:13, fontWeight:700, fill:'var(--color-text-primary)' }}>
                  {sliceTotal >= 1e6 ? (sliceTotal / 1e6).toFixed(2) + 'M' : sliceTotal >= 1e3 ? (sliceTotal / 1e3).toFixed(0) + 'K' : sliceTotal.toFixed(0)}
                </text>
              </svg>
              <div style={{ flex:1, minWidth:140 }}>
                {slices.map(s => (
                  <div key={s.label} style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, marginBottom:6 }}>
                    <span style={{ width:10, height:10, borderRadius:3, background:s.color, flexShrink:0 }} />
                    <span style={{ flex:1, color:'var(--color-neutral-700)' }}>{s.label}</span>
                    <span style={{ fontWeight:600 }}>{((s.value / sliceTotal) * 100).toFixed(0)}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Weight flow */}
        <div style={dashCard}>
          <div style={dashTitle}>Where the kilos went</div>
          <BarRow label="Goods bought" value={bought} max={bought} text={kg(bought)} color="var(--color-primary)" />
          <BarRow label="Good goods shipped" value={shipped} max={bought} text={kg(shipped)} color="var(--color-success)" />
          <BarRow label="Waste that can be sold" value={n(pnl.sellableWasteKg)} max={bought} text={kg(pnl.sellableWasteKg)} color="var(--color-warning)" />
          <BarRow label="Waste thrown away" value={n(pnl.unsellableWasteKg)} max={bought} text={kg(pnl.unsellableWasteKg)} color="var(--color-error)" />
          <BarRow label="Missing / unaccounted" value={Math.max(shrink, 0)} max={bought} text={kg(shrink)} color="var(--color-text-tertiary)" />
        </div>

        {/* Per-kg economics + buyers */}
        <div style={dashCard}>
          <div style={dashTitle}>{buyers.length ? 'Biggest buyers (kg shipped)' : 'Cost and sales per kg'}</div>
          {buyers.length ? buyers.map(b => (
            <BarRow key={b.buyerName} label={b.buyerName} value={n(b.weightKg)} max={buyerMax}
              text={`${n(b.weightKg).toLocaleString()} kg`} color="var(--color-accent)" />
          )) : (
            <>
              <BarRow label="Cost per kg" value={n(pnl.costPerKgExported)} max={Math.max(n(pnl.costPerKgExported), n(pnl.revenuePerKg), 1)} text={`PKR ${n(pnl.costPerKgExported).toFixed(2)}`} color="var(--color-warning)" />
              <BarRow label="Sales per kg" value={n(pnl.revenuePerKg)} max={Math.max(n(pnl.costPerKgExported), n(pnl.revenuePerKg), 1)} text={`PKR ${n(pnl.revenuePerKg).toFixed(2)}`} color="var(--color-info)" />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BatchManagement;
